import {
  Calendar,
  CheckCircle,
  Eye,
  EyeOff,
  FolderGit2,
  Key,
  Loader2,
  Lock,
  Mail,
  Pencil,
  Plus,
  RotateCcw,
  Save,
  Sparkles,
  Settings,
  Trash2,
  User,
  Users,
  XCircle,
  CreditCard,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuditLogSection } from '../../components/AuditLogSection';
import { CustomSelect } from '../../shared/components/CustomSelect';
import { AddProductModal } from '../../components/AddProductModal/AddProductModal';
import { CollapsibleInfoBlock } from '../../components/CollapsibleInfoBlock';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import type { Product } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { usePermission } from '../../shared/hooks/usePermission';
import { can, getVisibleProductsForOrgUser } from '../../shared/permissions';
import { apiFetch as apiFetchBase } from '../../shared/apiClient';
import { resetAllOnboarding } from '../../shared/onboarding/onboardingState';
import { productCompactDisplayLabel } from '../../shared/utils/productDisplayLabel';
import {
  AnalyticsConnectTourBody,
  analyticsMission,
  GuidedTourCard,
  useAtlasOptional,
  useGuidedTour,
} from '../../features/atlas';
import { CommitmentSourceConfigSection } from '../../features/commitment/CommitmentSourceConfigSection';
import { ForecastCustomFieldsSection } from '../../features/customFields/ForecastCustomFieldsSection';
import { JiraRoleFieldsSection } from '../../features/jiraRoleFields/JiraRoleFieldsSection';
import { ProductBrandingPanel } from './team/panels/branding/ProductBrandingPanel';
import { QualitySettingsPanel } from '../../features/analytics/quality/settings/QualitySettingsPanel';
import '../../components/AddProductModal/AddProductModal.css';
import { SettingsSidebar } from './SettingsSidebar';
import type { SettingsSection } from './SettingsSidebar';
import { TeamTab } from './team/TeamTab';
import { TeamTabAnchorStrip } from './team/TeamTabAnchorStrip';
import type { TeamTabContextValue } from './team/TeamTabContext';
import { InviteToOrgPanel } from './team/panels/access/InviteToOrgPanel';
import { OrgRbacMembersPanel } from './team/panels/access/OrgRbacMembersPanel';
import { CapacityZonePanel } from './team/panels/capacity/CapacityZonePanel';
import { CapacityScoringPanel } from './team/panels/capacity/CapacityScoringPanel';
import { CommitmentScoringPanel } from './team/panels/commitment/CommitmentScoringPanel';
import { EstimationTargetPanel } from './team/panels/estimation/EstimationTargetPanel';
import { PillarWeightsPanel } from './team/panels/pillarWeights/PillarWeightsPanel';
import { SuggestPeoplePanel } from './team/panels/people/SuggestPeoplePanel';
import { AddTeamMemberPanel } from './team/panels/people/AddTeamMemberPanel';
import { RolesAndCategoriesPanel } from './team/panels/people/RolesAndCategoriesPanel';
import { TeamRosterPanel } from './team/panels/people/TeamRosterPanel';
import './SettingsPage.css';
import './ProfilePage.css';

interface ProfileData {
  name: string;
  email: string;
  jiraToken: string;
  jiraProject: string;
  /** Jira site domain from org config (read-only hint in per-product section) */
  jiraDomain?: string;
  sharePointApiKey: string; // Legacy field
  sharePointSiteUrl: string;
  sharePointListId?: string;
  sharePointViewId?: string;
  sharePointRootFolder?: string;
  // Azure AD / Microsoft Graph API configuration
  sharePointClientId?: string;
  sharePointTenantId?: string;
  sharePointObjectId?: string;
  sharePointClientSecret?: string;
}

interface TeamMember {
  id?: number;
  name: string;
  email?: string;
  jiraUsername?: string;
  capacityHoursPerDay: number;
  isActive: boolean;
  includeInCapacity?: boolean;
  role?: string;
  /** Jira account active status: true = active, false = deactivated, null/undefined = unknown */
  jiraActive?: boolean | null;
  /** Include in historical capacity (past releases) */
  includeInHistoricalCapacity?: boolean;
  /** Start date in team (YYYY-MM-DD); used for historical capacity period */
  startDateInTeam?: string | null;
  /** End date in team (YYYY-MM-DD); empty = still in team */
  endDateInTeam?: string | null;
  /** Distribution criteria type for Forecast (legacy, single type) */
  distributionCriteriaType?: string | null;
  /** Selected values for the criteria type (legacy) */
  distributionCriteriaValues?: string[] | null;
  /** Full criteria: built-in fields + an open-ended `customFields` map keyed by
   *  `org_custom_fields.id` (number). The custom-field map is populated from any
   *  field flagged `inDistribution=true` in Settings → Project → Forecast custom
   *  fields. `cluster` is kept on the type for backward-compat with rows persisted
   *  before cluster was demoted to a custom field, but the UI no longer renders
   *  it (the suggest-distribution algorithm already ignores it). */
  distributionCriteria?: {
    cluster?: string[];
    component: string[];
    label: string[];
    priority: string[];
    customFields?: Record<string, string[]>;
  } | null;
  /** Exclude from forecast distribution (suggest-distribution) */
  excludeFromDistribution?: boolean;
  /** Present when roster is loaded via `/api/roster/persons` (PATCH via person id). */
  personId?: string;
}

/** Postgres `team_members` / roster-derived rows → UI `TeamMember` (camelCase). */
function normalizeTeamMembers(members: unknown[]): TeamMember[] {
  return (members || []).map((raw) => {
    const m = raw as Record<string, unknown>;

    const includeInCapRaw = (m.includeInCapacity ?? m.include_in_capacity) as unknown;

    let includeInCapacity: boolean;
    if (typeof includeInCapRaw === 'number') {
      includeInCapacity = includeInCapRaw === 1;
    } else if (typeof includeInCapRaw === 'boolean') {
      includeInCapacity = includeInCapRaw;
    } else if (includeInCapRaw == null) {
      includeInCapacity = true;
    } else {
      includeInCapacity = true;
    }

    const jiraActiveRaw = m.jiraActive ?? m.jira_active;
    let jiraActive: boolean | null = null;
    if (jiraActiveRaw === 1 || jiraActiveRaw === true) jiraActive = true;
    else if (jiraActiveRaw === 0 || jiraActiveRaw === false) jiraActive = false;

    const histRaw = m.includeInHistoricalCapacity ?? m.include_in_historical_capacity;
    const includeInHistoricalCapacity = histRaw === 1 || histRaw === true;

    const exclRaw = m.excludeFromDistribution ?? m.exclude_from_distribution;
    const excludeFromDistribution = exclRaw === 1 || exclRaw === true;

    const startRaw = (m.startDateInTeam ?? m.start_date_in_team) as unknown;
    const endRaw = (m.endDateInTeam ?? m.end_date_in_team) as unknown;
    const startDateInTeam =
      startRaw != null && String(startRaw).trim() !== '' ? String(startRaw).trim() : null;
    const endDateInTeam =
      endRaw != null && String(endRaw).trim() !== '' ? String(endRaw).trim() : null;

    const capRaw = (m.capacityHoursPerDay ?? m.capacity_hours_per_day) as unknown;
    const capacityHoursPerDay = typeof capRaw === 'number' && !Number.isNaN(capRaw) ? capRaw : 8.0;

    const emptyCriteria = {
      component: [] as string[],
      label: [] as string[],
      priority: [] as string[],
      customFields: {} as Record<string, string[]>,
    };

    const normalizeCustomFieldsMap = (cfs: unknown): Record<string, string[]> => {
      if (!cfs || typeof cfs !== 'object' || Array.isArray(cfs)) return {};
      const out: Record<string, string[]> = {};
      for (const [k, v] of Object.entries(cfs as Record<string, unknown>)) {
        if (Array.isArray(v)) {
          out[k] = v.filter((x): x is string => typeof x === 'string');
        }
      }
      return out;
    };

    let distributionCriteria: TeamMember['distributionCriteria'] = null;
    const dcObj =
      m.distributionCriteria != null && typeof m.distributionCriteria === 'object'
        ? (m.distributionCriteria as Record<string, unknown>)
        : null;
    if (dcObj) {
      distributionCriteria = {
        component: Array.isArray(dcObj.component) ? (dcObj.component as string[]) : [],
        label: Array.isArray(dcObj.label) ? (dcObj.label as string[]) : [],
        priority: Array.isArray(dcObj.priority) ? (dcObj.priority as string[]) : [],
        customFields: normalizeCustomFieldsMap(dcObj.customFields),
      };
    } else if (m.distribution_criteria != null) {
      try {
        const parsed =
          typeof m.distribution_criteria === 'string'
            ? JSON.parse(m.distribution_criteria as string)
            : m.distribution_criteria;
        if (parsed && typeof parsed === 'object') {
          const p = parsed as Record<string, unknown>;
          distributionCriteria = {
            component: Array.isArray(p.component) ? (p.component as string[]) : [],
            label: Array.isArray(p.label) ? (p.label as string[]) : [],
            priority: Array.isArray(p.priority) ? (p.priority as string[]) : [],
            customFields: normalizeCustomFieldsMap(p.customFields),
          };
        }
      } catch {
        distributionCriteria = null;
      }
    }

    const idRaw = m.id;
    let idNum: number | undefined;
    if (typeof idRaw === 'number' && Number.isFinite(idRaw)) idNum = idRaw;
    else if (typeof idRaw === 'string' && idRaw.trim() !== '') {
      const parsed = Number.parseInt(idRaw, 10);
      if (Number.isFinite(parsed)) idNum = parsed;
    }

    const nameStr = typeof m.name === 'string' ? m.name : String(m.name ?? '');
    const emailStr =
      typeof m.email === 'string' ? m.email : m.email != null ? String(m.email) : undefined;

    const jiraU =
      (m.jiraUsername as string | undefined) ??
      (m.jira_username as string | undefined) ??
      undefined;

    const isActive = (m.isActive ?? m.is_active) !== false;

    const personIdRaw = m.personId ?? m.person_id;
    let personId: string | undefined;
    if (typeof personIdRaw === 'string') personId = personIdRaw;
    else if (personIdRaw != null) personId = String(personIdRaw);

    return {
      ...(m as object),
      id: idNum,
      name: nameStr ?? '',
      email: emailStr ?? undefined,
      jiraUsername: jiraU ?? undefined,
      capacityHoursPerDay,
      isActive,
      includeInCapacity,
      jiraActive,
      includeInHistoricalCapacity,
      excludeFromDistribution,
      startDateInTeam: startDateInTeam ?? undefined,
      endDateInTeam: endDateInTeam ?? undefined,
      role: typeof m.role === 'string' && m.role.trim() !== '' ? m.role.trim() : 'developer',
      distributionCriteria: distributionCriteria ?? emptyCriteria,
      ...(personId ? { personId } : {}),
    } as TeamMember;
  });
}

function rawTeamMemberFromRosterSlice(
  person: Record<string, unknown>,
  slice: Record<string, unknown>
): Record<string, unknown> {
  const rowName =
    slice.nameOnRow != null && String(slice.nameOnRow).trim() !== ''
      ? String(slice.nameOnRow).trim()
      : String(person.display_name || '').trim();
  return {
    id: slice.teamMemberId,
    name: rowName,
    email: slice.emailOnRow ?? person.email ?? null,
    jira_username: slice.jiraUsername ?? person.jira_username ?? null,
    jira_active: slice.jiraActive,
    capacity_hours_per_day: slice.capacityHoursPerDay,
    is_active: slice.isActive,
    include_in_capacity: slice.includeInCapacity,
    include_in_historical_capacity: slice.includeInHistoricalCapacity,
    exclude_from_distribution: slice.excludeFromDistribution,
    role: slice.role ?? 'developer',
    start_date_in_team: slice.startDateInTeam,
    end_date_in_team: slice.endDateInTeam,
    distribution_criteria_type: slice.distributionCriteriaType,
    distribution_criteria_values: slice.distributionCriteriaValues,
    distribution_criteria: slice.distributionCriteria,
  };
}

function teamMembersForProductFromRoster(
  persons: Record<string, unknown>[],
  productId: string | undefined | null
): TeamMember[] {
  if (!productId) return [];
  const pid = String(productId);
  const out: TeamMember[] = [];
  for (const person of persons) {
    const slices = Array.isArray(person.products) ? person.products : [];
    const slice = slices.find((s: Record<string, unknown>) => String(s.productId) === pid);
    if (!slice) continue;
    const raw = rawTeamMemberFromRosterSlice(person, slice as Record<string, unknown>);
    const [tm] = normalizeTeamMembers([raw]);
    const personUuid =
      typeof person.id === 'string' ? person.id : person.id != null ? String(person.id) : '';
    if (personUuid && tm.id != null) {
      out.push({ ...tm, personId: personUuid });
    }
  }
  out.sort((a, b) =>
    String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' })
  );
  return out;
}

function activeProductIdsFromPerson(person: Record<string, unknown>): string[] {
  const slices = Array.isArray(person.products) ? person.products : [];
  const ids = slices
    .filter((s: Record<string, unknown>) => s.isActive !== false)
    .map((s: Record<string, unknown>) =>
      typeof s.productId === 'string' ? s.productId : s.productId != null ? String(s.productId) : ''
    )
    .filter(Boolean);
  return Array.from(new Set(ids));
}

function serializeDistributionCriteriaForRoster(teamMember: TeamMember): string | null {
  const dc = teamMember.distributionCriteria;
  if (!dc || typeof dc !== 'object') return null;
  const cfRaw = dc.customFields;
  const parsedCf =
    cfRaw != null && typeof cfRaw === 'object' && !Array.isArray(cfRaw)
      ? (cfRaw as Record<string, unknown>)
      : {};
  const keysOk = Object.keys(parsedCf).every((k) => typeof k === 'string');
  return JSON.stringify({
    cluster: Array.isArray(dc.cluster) ? dc.cluster : [],
    component: Array.isArray(dc.component) ? dc.component : [],
    label: Array.isArray(dc.label) ? dc.label : [],
    priority: Array.isArray(dc.priority) ? dc.priority : [],
    customFields: keysOk
      ? Object.fromEntries(
          Object.entries(parsedCf).map(([k, v]) => [
            k,
            Array.isArray(v) ? v.filter((x) => typeof x === 'string') : [],
          ])
        )
      : {},
  });
}

/** Owner-only RBAC table: organization_members + auth email confirmation. */
interface OrgRbacMemberProduct {
  productId: string;
  name: string;
  jiraProjectKey: string | null;
}

interface OrgRbacMemberRow {
  user_id: string;
  role: string;
  joined_at?: string | null;
  email?: string | null;
  email_confirmed_at?: string | null;
  display_name?: string | null;
  avatar_url?: string | null;
  products?: OrgRbacMemberProduct[] | null;
}

/** Postgres API returns snake_case; SPA historically used camelCase — normalize for UI state. */
function roleIncludeInCapacityMappingFromApi(r: {
  includeInCapacityMapping?: boolean;
  include_in_capacity_mapping?: boolean;
}): boolean {
  const v = r.include_in_capacity_mapping ?? r.includeInCapacityMapping;
  return v !== false;
}

function roleCapacityCategoryFromApi(r: {
  capacityCategory?: string;
  capacity_category?: string;
}): string {
  const raw = r.capacity_category ?? r.capacityCategory;
  const s = typeof raw === 'string' ? raw.trim() : '';
  return s || 'developer';
}

function orgRoleLabel(role: string | undefined): string {
  if (!role) return 'Member';
  const r = role.toLowerCase();
  if (r === 'owner') return 'Owner';
  if (r === 'administrator') return 'Administrator';
  if (r === 'member') return 'Member';
  return role;
}

/** Platform support staff: show empty name until they save a real display name; hide mistaken email-as-name. */
function supportStaffPersonalName(
  displayName: string | null | undefined,
  loginEmail: string | undefined
): string {
  const dn = typeof displayName === 'string' ? displayName.trim() : '';
  if (!dn) return '';
  const em = (loginEmail || '').trim().toLowerCase();
  if (em && dn.toLowerCase() === em) return '';
  return dn;
}

export const ProfilePage: React.FC = () => {
  const {
    user,
    profile: authProfile,
    currentOrg,
    currentProduct,
    effectiveRole,
    refreshUser,
    selectProduct,
    changePassword,
    sendPasswordResetEmail,
    isSupportUser,
  } = useAuth();
  const { allowed: canUpdateOrg } = usePermission('organization.update');
  const { allowed: canCreateProduct } = usePermission('product.create');
  const { allowed: canDeleteProduct } = usePermission('product.delete');
  const { allowed: canUpdateProduct } = usePermission('product.update');
  const { allowed: canViewBilling } = usePermission('billing.view');
  const { allowed: canModifyBilling } = usePermission('billing.modify');
  const { allowed: canConfigureIntegrations } = usePermission('integrations.configure');
  const { allowed: canViewTeam } = usePermission('team.view');
  const { allowed: canModifyTeam } = usePermission('team.modify');
  const { allowed: canManageOrgTeam } = usePermission('team.manage_org_team');
  /**
   * 3-role model (PR1): member sees only the "My Profile" tab. The Project /
   * Team / Integrations / Billing tabs live behind `settings.view_org` so that
   * org-wide configuration stays owner/admin. This flag gates both the tab
   * buttons AND the forced-tab-switch effect below.
   */
  const { allowed: canViewOrgSettings } = usePermission('settings.view_org');
  const { subscription, usage, refresh: refreshSubscription } = useSubscription();
  const { theme: _theme } = useTheme();
  const navigate = useNavigate();
  const [onboardingResetMessage, setOnboardingResetMessage] = useState<string | null>(null);
  const [atlasRestoreMessage, setAtlasRestoreMessage] = useState<string | null>(null);
  const atlas = useAtlasOptional();
  const [passwordCurrent, setPasswordCurrent] = useState('');
  const [passwordNew, setPasswordNew] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPasswordCurrent, setShowPasswordCurrent] = useState(false);
  const [showPasswordNew, setShowPasswordNew] = useState(false);
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [resetEmailSending, setResetEmailSending] = useState(false);
  const [passwordSectionMessage, setPasswordSectionMessage] = useState<string | null>(null);
  const [passwordSectionError, setPasswordSectionError] = useState<string | null>(null);

  const profileApiFetch = useCallback(
    (path: string, init?: RequestInit) => {
      const headers = new Headers(init?.headers);
      const orgId = currentOrg?.id;
      if (orgId && !headers.has('X-Organization-Id')) {
        headers.set('X-Organization-Id', orgId);
      }
      const productId = currentProduct?.id;
      if (productId && !headers.has('X-Product-Id')) {
        headers.set('X-Product-Id', productId);
      }
      return apiFetchBase(path, { ...init, headers });
    },
    [currentOrg?.id, currentProduct?.id]
  );
  const [profile, setProfile] = useState<ProfileData>({
    name: '',
    email: '',
    jiraToken: '',
    jiraProject: '',
    sharePointApiKey: '',
    sharePointSiteUrl: '',
    sharePointListId: '',
    sharePointViewId: '',
    sharePointRootFolder: '',
    sharePointClientId: '',
    sharePointTenantId: '',
    sharePointObjectId: '',
    sharePointClientSecret: '',
  });
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [originalTeamMembers, setOriginalTeamMembers] = useState<TeamMember[]>([]); // Исходные данные для сравнения
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(true);
  const [teamSuggestionsLoading, setTeamSuggestionsLoading] = useState(false);
  const [teamSuggestionsError, setTeamSuggestionsError] = useState<string | null>(null);
  const [teamSuggestionsData, setTeamSuggestionsData] = useState<{
    candidates: Array<{
      displayName: string;
      totalTouches: number;
      lastSeen: string | null;
      touchesByProduct: Record<string, number>;
    }>;
    existingMatches: Array<{
      displayName: string;
      totalTouches: number;
      lastSeen: string | null;
      touchesByProduct: Record<string, number>;
      existingPersonId: string;
    }>;
    products: Array<{ id: string; name: string; jiraProjectKey: string | null }>;
    filters: { sinceMonths: number; minTouches: number; sinceCutoff: string };
  } | null>(null);
  const [teamSuggestionSelected, setTeamSuggestionSelected] = useState<Set<string>>(new Set());
  const [teamSuggestionProductOverrides, setTeamSuggestionProductOverrides] = useState<
    Record<string, string[]>
  >({});
  const [expandedSuggestions, setExpandedSuggestions] = useState<Set<string>>(new Set());
  const [savingCapacity, setSavingCapacity] = useState(false);
  const [newMember, setNewMember] = useState<Partial<TeamMember>>({
    name: '',
    email: '',
    jiraUsername: '',
    capacityHoursPerDay: 8.0,
    role: 'developer',
    includeInHistoricalCapacity: false,
    excludeFromDistribution: false,
    startDateInTeam: undefined,
    endDateInTeam: undefined,
  });
  const [availableRoles, setAvailableRoles] = useState<string[]>(['developer', 'qa']);
  const [rolesList, setRolesList] = useState<
    { id: number; name: string; includeInCapacityMapping?: boolean; capacityCategory?: string }[]
  >([]);
  const [showAddRoleInput, setShowAddRoleInput] = useState(false);
  const [showManageRoles, setShowManageRoles] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [editingRoleId, setEditingRoleId] = useState<number | null>(null);
  const [editingRoleName, setEditingRoleName] = useState('');
  const newRoleInputRef = useRef<HTMLInputElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);

  const reloadTeamMembers = useCallback(async () => {
    if (!currentOrg?.id) {
      setTeamMembers([]);
      setOriginalTeamMembers([]);
      setRosterPersonsList([]);
      setRosterFetchError(null);
      setLoadingTeamMembers(false);
      return;
    }
    if (!canViewTeam) {
      setLoadingTeamMembers(false);
      return;
    }
    if (!canManageOrgTeam) {
      setTeamMembers([]);
      setOriginalTeamMembers([]);
      setRosterPersonsList([]);
      setRosterFetchError(null);
      setLoadingTeamMembers(false);
      return;
    }
    try {
      setLoadingTeamMembers(true);
      setRosterFetchError(null);
      const rosterRes = await profileApiFetch('/api/roster/persons');
      if (!rosterRes.ok) {
        const errJson = await rosterRes.json().catch(() => ({}));
        const msg =
          typeof (errJson as { error?: string }).error === 'string'
            ? String((errJson as { error?: string }).error)
            : `Failed to load roster (${rosterRes.status})`;
        setRosterFetchError(msg);
        setTeamMembers([]);
        setOriginalTeamMembers([]);
        setRosterPersonsList([]);
        console.error('GET /api/roster/persons failed', rosterRes.status, msg);
        return;
      }
      const rosterJson = await rosterRes.json();
      const persons = (rosterJson.persons || []) as Record<string, unknown>[];
      setRosterPersonsList(persons);
      const normalized = teamMembersForProductFromRoster(persons, currentProduct?.id);
      setTeamMembers(normalized);
      setOriginalTeamMembers(
        normalized.map((m) => ({
          ...m,
          includeInCapacity: m.includeInCapacity,
        }))
      );

      try {
        const rolesRes = await profileApiFetch('/api/roles');
        if (rolesRes.ok) {
          const rolesData = await rolesRes.json();
          const list = rolesData.roles || [];
          const roleNames = list.map((r: { name: string }) => r.name);
          if (roleNames.length > 0) {
            setAvailableRoles(roleNames);
            setRolesList(
              list.map(
                (r: {
                  id: number;
                  name: string;
                  includeInCapacityMapping?: boolean;
                  include_in_capacity_mapping?: boolean;
                }) => ({
                  id: r.id,
                  name: r.name,
                  includeInCapacityMapping: roleIncludeInCapacityMappingFromApi(r),
                })
              )
            );
          } else {
            const rolesSet = new Set<string>(['developer', 'qa']);
            normalized.forEach((m) => {
              if (m.role && m.role.trim() !== '') rolesSet.add(m.role.trim());
            });
            setAvailableRoles(Array.from(rolesSet).sort());
          }
        } else {
          const rolesSet = new Set<string>(['developer', 'qa']);
          normalized.forEach((m) => {
            if (m.role && m.role.trim() !== '') rolesSet.add(m.role.trim());
          });
          setAvailableRoles(Array.from(rolesSet).sort());
        }
      } catch {
        const rolesSet = new Set<string>(['developer', 'qa']);
        normalized.forEach((m) => {
          if (m.role && m.role.trim() !== '') rolesSet.add(m.role.trim());
        });
        setAvailableRoles(Array.from(rolesSet).sort());
      }
      // eslint-disable-next-line no-console
      console.log('📋 Team roster loaded (product slice):', normalized.length, 'rows');
    } catch (err) {
      console.error('Error loading team roster:', err);
    } finally {
      setLoadingTeamMembers(false);
    }
  }, [profileApiFetch, currentOrg?.id, currentProduct?.id, canViewTeam, canManageOrgTeam]);

  // Estimation units (for Capacity & Forecast)
  const [projectSettings, setProjectSettings] = useState<{
    estimationUnit: 'hours_per_day' | 'story_points_per_day';
    estimationHoursPerDay: number;
    workingDays: number[];
  }>({
    estimationUnit: 'hours_per_day',
    estimationHoursPerDay: 8,
    workingDays: [1, 2, 3, 4, 5],
  });
  const [loadingProjectSettings, setLoadingProjectSettings] = useState(false);
  const [savingProjectSettings, setSavingProjectSettings] = useState(false);

  const [showToken, setShowToken] = useState(false);
  const [showSharePointToken, setShowSharePointToken] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [orgDisplayName, setOrgDisplayName] = useState('');
  const [orgNameSaveError, setOrgNameSaveError] = useState<string | null>(null);
  const [orgNameSaving, setOrgNameSaving] = useState(false);
  const [orgNameSaved, setOrgNameSaved] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'administrator' | 'member'>('member');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteMessage, setInviteMessage] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  /** Empty array = invite uses server default product(s). */
  const [inviteOrgProductIds, setInviteOrgProductIds] = useState<string[]>([]);
  const [rosterPersonsList, setRosterPersonsList] = useState<Record<string, unknown>[]>([]);
  const [rosterFetchError, setRosterFetchError] = useState<string | null>(null);
  const [rosterProductToggleUserId, setRosterProductToggleUserId] = useState<string | null>(null);
  const [addMemberRosterPick, setAddMemberRosterPick] = useState<Record<string, unknown> | null>(
    null
  );
  const [addMemberSearchResults, setAddMemberSearchResults] = useState<
    Array<Record<string, unknown>>
  >([]);
  const [addMemberSearchLoading, setAddMemberSearchLoading] = useState(false);
  const [rosterInviteModalPerson, setRosterInviteModalPerson] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [rosterInviteEmail, setRosterInviteEmail] = useState('');
  const [rosterInviteRole, setRosterInviteRole] = useState<'administrator' | 'member'>('member');
  const [rosterInviteProductIds, setRosterInviteProductIds] = useState<string[]>([]);
  const [rosterInviteLoading, setRosterInviteLoading] = useState(false);
  const [rosterInviteError, setRosterInviteError] = useState<string | null>(null);
  const [orgMemberRemoveTarget, setOrgMemberRemoveTarget] = useState<OrgRbacMemberRow | null>(null);
  const [orgMemberRemoveLoading, setOrgMemberRemoveLoading] = useState(false);

  /** Owner-only: organization login members + RBAC (API: rbac-admin). */
  const [orgRbacMembers, setOrgRbacMembers] = useState<OrgRbacMemberRow[]>([]);
  const [orgRbacLoading, setOrgRbacLoading] = useState(false);
  const [orgRbacError, setOrgRbacError] = useState<string | null>(null);
  const [orgRbacRoleDraft, setOrgRbacRoleDraft] = useState<Record<string, string>>({});
  const [orgRbacSavingUserId, setOrgRbacSavingUserId] = useState<string | null>(null);
  const [orgRbacOwnershipConfirm, setOrgRbacOwnershipConfirm] = useState<{
    userId: string;
    displayLabel: string;
  } | null>(null);

  const [projectSettingsSaveError, setProjectSettingsSaveError] = useState<string | null>(null);
  const [testingSharePoint, setTestingSharePoint] = useState(false);
  const [sharePointTestResult, setSharePointTestResult] = useState<string | null>(null);

  /** Project Settings: per-product integration editing */
  const [settingsProductId, setSettingsProductId] = useState<string | null>(null);
  const [productDraft, setProductDraft] = useState({
    name: '',
    jira_project_key: '',
    sharepoint_calendar_name: '',
    sharepoint_calendar_url: '',
    sharepoint_list_id: '',
  });
  const [productIntegrationSaving, setProductIntegrationSaving] = useState(false);
  const [jiraSectionSaving, setJiraSectionSaving] = useState(false);
  const [sharePointSectionSaving, setSharePointSectionSaving] = useState(false);
  const [jiraSectionSaved, setJiraSectionSaved] = useState(false);
  const [sharePointSectionSaved, setSharePointSectionSaved] = useState(false);
  const [jiraSectionError, setJiraSectionError] = useState<string | null>(null);
  const [sharePointSectionError, setSharePointSectionError] = useState<string | null>(null);
  const [productIntegrationError, setProductIntegrationError] = useState<string | null>(null);
  const [productIntegrationSaved, setProductIntegrationSaved] = useState(false);
  /** Inline green "Saved" banners for the Team tab (replace alert() popups). */
  const [teamRosterSaved, setTeamRosterSaved] = useState(false);
  const [teamRosterSaveError, setTeamRosterSaveError] = useState<string | null>(null);
  const [savingDistributionCriteria, setSavingDistributionCriteria] = useState(false);
  const [distributionCriteriaSaved, setDistributionCriteriaSaved] = useState(false);
  const [distributionCriteriaSaveError, setDistributionCriteriaSaveError] = useState<string | null>(
    null
  );
  /** Inline "Saved" banner for Estimation hours / Working days (project_settings). */
  const [projectSettingsSaved, setProjectSettingsSaved] = useState(false);
  const [addProductModalOpen, setAddProductModalOpen] = useState(false);
  const [deleteConfirmProduct, setDeleteConfirmProduct] = useState<Product | null>(null);
  const [productDeleteLoading, setProductDeleteLoading] = useState(false);
  const [testingProductCalendar, setTestingProductCalendar] = useState(false);
  const [productCalendarTestResult, setProductCalendarTestResult] = useState<string | null>(null);
  /** Sync with ?tab= / ?section= on first paint so Save validation matches visible section. */
  const [activeTab, setActiveTab] = useState<
    'profile' | 'project' | 'integrations' | 'team' | 'billing'
  >(() => {
    if (typeof window === 'undefined') return 'profile';
    const params = new URLSearchParams(window.location.search);
    const s = params.get('section');
    if (s === 'organization') return 'integrations'; // Organization is a sub-section of Integrations
    if (s === 'profile' || s === 'integrations' || s === 'team' || s === 'billing') return s;
    // Backward compat: ?tab=project maps to ?section=integrations
    const t = params.get('tab');
    if (t === 'team' || t === 'profile' || t === 'billing') return t;
    if (t === 'project') return 'integrations';
    return 'profile';
  });
  /**
   * Members who deep-link to /settings?section=organization etc.
   * are bounced back to the profile section. Permission check is still enforced
   * on the server; this prevents them from seeing an empty org-section while
   * the data loads.
   */
  useEffect(() => {
    if (!canViewOrgSettings && activeTab !== 'profile') {
      setActiveTab('profile');
      try {
        setSearchParams({ section: 'profile' });
      } catch {
        // searchParams writer can throw during unmount; ignore
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewOrgSettings, activeTab]);
  const [billingInvoices, setBillingInvoices] = useState<
    Array<{
      id: string;
      amount_cents: number;
      currency: string;
      status: string;
      invoice_pdf_url: string | null;
      period_start: string;
      period_end: string;
      created_at: string;
    }>
  >([]);
  const [billingInvoicesLoading, setBillingInvoicesLoading] = useState(false);
  const [billingPortalLoading, setBillingPortalLoading] = useState(false);
  const [billingStripeSyncing, setBillingStripeSyncing] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const analyticsAtlasTour = useGuidedTour({
    mission: analyticsMission,
    anchorExtraDeps: [activeTab],
  });
  /** Last Atlas step id seen on Settings — prevents trapping the user on Integrations when the tour stays on `connect`. */
  const analyticsAtlasSettingsPrevStepIdRef = useRef<string | undefined>(undefined);
  /** On-screen + window.__settingsSaveDiag — console.info is often hidden by default log filters. */
  const [saveDiagLines, setSaveDiagLines] = useState<string[]>([]);
  const pushSaveDiag = useCallback((line: string) => {
    const msg = `${new Date().toISOString().slice(11, 23)} ${line}`;
    setSaveDiagLines((prev) => [...prev.slice(-15), msg]);
    // eslint-disable-next-line no-console
    console.log('[SettingsSave]', msg);
    if (typeof window !== 'undefined') {
      const w = window as Window & { __settingsSaveDiag?: string[] };
      w.__settingsSaveDiag = [...(w.__settingsSaveDiag || []).slice(-25), msg];
    }
  }, []);

  /** Distribution criteria dropdowns are populated from /api/forecast/meta. The built-in
   *  fields (components / labels / priorities) come from `issues`; `customFields` carries
   *  the per-product custom-field catalog with `inDistribution=true` so we can render
   *  one extra dropdown per such field with its distinct values from `issue_custom_fields`. */
  const [criteriaOptions, setCriteriaOptions] = useState<{
    components: string[];
    labels: string[];
    priorities: string[];
    customFields: Array<{
      orgFieldId: number;
      jiraFieldId: string;
      displayName: string;
      distinctValues: string[];
    }>;
  }>({
    components: [],
    labels: [],
    priorities: ['Highest', 'High', 'Medium', 'Low', 'Lowest'],
    customFields: [],
  });
  const [openCriteriaDropdown, setOpenCriteriaDropdown] = useState<string | null>(null);
  const [criteriaSearchQueries, setCriteriaSearchQueries] = useState<Record<string, string>>({});
  const criteriaDropdownRef = React.useRef<HTMLDivElement>(null);
  const sharePointOrgSectionRef = useRef<HTMLDivElement>(null);
  const [highlightSharePointOrgSection, setHighlightSharePointOrgSection] = useState(false);
  const estimationUnitsSectionRef = useRef<HTMLDivElement>(null);
  const estimationTargetSectionRef = useRef<HTMLDivElement>(null);
  const [highlightEstimationTargetSection, setHighlightEstimationTargetSection] = useState(false);
  const [highlightEstimationUnitsSection, setHighlightEstimationUnitsSection] = useState(false);
  const [highlightCapacityScoringSection, setHighlightCapacityScoringSection] = useState(false);
  const distributionCriteriaSectionRef = useRef<HTMLDivElement>(null);
  const [highlightDistributionCriteriaSection, setHighlightDistributionCriteriaSection] =
    useState(false);
  const teamRosterSectionRef = useRef<HTMLDivElement>(null);
  const [highlightTeamRosterSection, setHighlightTeamRosterSection] = useState(false);
  const forecastCustomFieldsSectionRef = useRef<HTMLDivElement>(null);
  const [highlightForecastCustomFieldsSection, setHighlightForecastCustomFieldsSection] =
    useState(false);
  const jiraRoleFieldsSectionRef = useRef<HTMLDivElement>(null);
  const [highlightJiraRoleFieldsSection, setHighlightJiraRoleFieldsSection] = useState(false);
  const teamAddMemberSectionRef = useRef<HTMLDivElement>(null);
  const [highlightTeamAddMemberSection, setHighlightTeamAddMemberSection] = useState(false);
  const profileQualitySettingsSectionRef = useRef<HTMLDivElement>(null);

  /** Team roster search, sort, and pagination state */
  const [rosterSearch, setRosterSearch] = useState('');
  const [rosterSortBy, setRosterSortBy] = useState<'name' | 'role' | 'capacity'>('name');
  const [rosterSortDir, setRosterSortDir] = useState<'asc' | 'desc'>('asc');
  const [rosterPage, setRosterPage] = useState(0);
  const ROSTER_PAGE_SIZE = 50;

  useEffect(() => {
    const section = searchParams.get('section');
    if (section === 'organization') {
      setActiveTab('integrations');
    } else if (
      section === 'profile' ||
      section === 'integrations' ||
      section === 'team' ||
      section === 'billing'
    ) {
      setActiveTab(section);
    } else {
      // Backward compat: ?tab=X
      const tab = searchParams.get('tab');
      if (tab === 'team' || tab === 'profile' || tab === 'billing') setActiveTab(tab);
      else if (tab === 'project') setActiveTab('integrations');
    }
  }, [searchParams]);

  useEffect(() => {
    if (canViewBilling || activeTab !== 'billing') return;
    setActiveTab('profile');
    try {
      setSearchParams({ section: 'profile' });
    } catch {
      // ignore
    }
  }, [canViewBilling, activeTab, setSearchParams]);

  /** Analytics Atlas “connect” step targets Jira under Organization & Integrations — switch once when entering the step so the anchor exists; do not override an explicit sidebar choice while the step remains `connect`. */
  useEffect(() => {
    if (
      !analyticsAtlasTour.hydrated ||
      analyticsAtlasTour.missingContext ||
      analyticsAtlasTour.isTerminal
    ) {
      analyticsAtlasSettingsPrevStepIdRef.current = undefined;
      return;
    }
    if (!canConfigureIntegrations) return;
    const stepId = analyticsAtlasTour.step?.id;
    const prevStepId = analyticsAtlasSettingsPrevStepIdRef.current;
    analyticsAtlasSettingsPrevStepIdRef.current = stepId;

    if (stepId !== 'connect') return;
    if (activeTab === 'integrations') return;
    if (prevStepId === 'connect') return;

    setActiveTab('integrations');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('section', 'integrations');
      return next;
    });
  }, [
    analyticsAtlasTour.hydrated,
    analyticsAtlasTour.missingContext,
    analyticsAtlasTour.isTerminal,
    analyticsAtlasTour.step?.id,
    activeTab,
    setSearchParams,
    canConfigureIntegrations,
  ]);

  /** Deep-link from Calendar (?tab=project&section=sharepoint): scroll to org SharePoint block and pulse highlight. */
  useEffect(() => {
    const section = searchParams.get('section');
    if (section !== 'sharepoint' || activeTab !== 'integrations' || !canConfigureIntegrations)
      return;

    let cancelled = false;
    let clearHighlight: number | undefined;
    let focusTimer: number | undefined;

    const run = () => {
      const el = sharePointOrgSectionRef.current;
      if (!el || cancelled) return false;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setHighlightSharePointOrgSection(true);
      const next = new URLSearchParams(searchParams);
      next.delete('section');
      setSearchParams(next, { replace: true });
      focusTimer = window.setTimeout(() => {
        document.getElementById('sharePointSiteUrl')?.focus({ preventScroll: true });
      }, 400);
      clearHighlight = window.setTimeout(() => setHighlightSharePointOrgSection(false), 5000);
      return true;
    };

    const t = window.setTimeout(() => {
      if (run()) return;
      requestAnimationFrame(() => {
        if (!cancelled) run();
      });
    }, 80);

    return () => {
      cancelled = true;
      window.clearTimeout(t);
      if (clearHighlight) window.clearTimeout(clearHighlight);
      if (focusTimer) window.clearTimeout(focusTimer);
    };
  }, [searchParams, activeTab, setSearchParams, canConfigureIntegrations]);

  /** Analytics Quality deep-links (?tab=project&section=quality-settings): Project tab scrolls to Quality mappings. */
  useEffect(() => {
    const section = searchParams.get('section');
    if (section !== 'quality-settings' || activeTab !== 'integrations') return;

    let cancelled = false;
    let pollId: number | undefined;
    let attempts = 0;
    const maxAttempts = 40;

    const run = () => {
      const el = profileQualitySettingsSectionRef.current;
      if (!el || cancelled) return false;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const next = new URLSearchParams(searchParams);
      next.delete('section');
      setSearchParams(next, { replace: true });
      return true;
    };

    const schedulePoll = (first: boolean) => {
      pollId = window.setTimeout(
        () => {
          if (cancelled) return;
          if (run()) return;
          attempts += 1;
          if (attempts < maxAttempts) schedulePoll(false);
        },
        first ? 60 : 50
      );
    };

    schedulePoll(true);

    return () => {
      cancelled = true;
      if (pollId) window.clearTimeout(pollId);
    };
  }, [searchParams, activeTab, setSearchParams]);

  /** Deep-link from Forecast guided tour (?tab=team&section=estimation-units): scroll to Estimation units block and pulse highlight. */
  useEffect(() => {
    const section = searchParams.get('section');
    if (section !== 'estimation-units' || activeTab !== 'team' || !canManageOrgTeam) return;

    let cancelled = false;
    let clearHighlight: number | undefined;

    const run = () => {
      const el = estimationUnitsSectionRef.current;
      if (!el || cancelled) return false;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setHighlightEstimationUnitsSection(true);
      const next = new URLSearchParams(searchParams);
      next.delete('section');
      setSearchParams(next, { replace: true });
      clearHighlight = window.setTimeout(() => setHighlightEstimationUnitsSection(false), 5000);
      return true;
    };

    const t = window.setTimeout(() => {
      if (run()) return;
      requestAnimationFrame(() => {
        if (!cancelled) run();
      });
    }, 80);

    return () => {
      cancelled = true;
      window.clearTimeout(t);
      if (clearHighlight) window.clearTimeout(clearHighlight);
    };
  }, [searchParams, activeTab, setSearchParams, canManageOrgTeam]);

  /** Deep-link from Delivery Review scoring breakdown (?tab=project&section=estimation-target):
   *  Scroll to the Estimation Target panel and pulse highlight.
   *  Note: ?tab=project maps to activeTab='integrations' internally.
   *  Uses continuous polling (up to 12s) because the panel is near the bottom of a long
   *  settings page and earlier panels may still be rendering/loading when the first scroll fires. */
  useEffect(() => {
    const section = searchParams.get('section');
    if (section !== 'estimation-target' || activeTab !== 'integrations') return;

    let cancelled = false;
    let pollId: number | undefined;
    let reapplyId1: number | undefined;
    let reapplyId2: number | undefined;
    let clearId: number | undefined;
    let attempts = 0;
    const maxAttempts = 60;

    const HEADER_OFFSET = 88;
    const SECTION_ID = 'profile-estimation-target';
    const HIGHLIGHT_CLS = 'profile-estimation-target-highlight';

    const scrollToSection = (el: HTMLElement) => {
      const rect = el.getBoundingClientRect();
      const target = (window.scrollY || window.pageYOffset || 0) + rect.top - HEADER_OFFSET;
      const scrollEl =
        (document.scrollingElement as HTMLElement | null) || document.documentElement;
      scrollEl.scrollTo({ top: Math.max(0, target), left: 0, behavior: 'auto' });
    };

    const fire = (el: HTMLElement) => {
      scrollToSection(el);
      setHighlightEstimationTargetSection(true);
      reapplyId1 = window.setTimeout(() => scrollToSection(el), 100);
      reapplyId2 = window.setTimeout(() => scrollToSection(el), 400);
      clearId = window.setTimeout(() => {
        setHighlightEstimationTargetSection(false);
      }, 8000);
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('section');
        window.history.replaceState({}, '', url.toString());
      } catch {
        /* noop */
      }
    };

    const poll = () => {
      if (cancelled) return;
      attempts++;
      const el = document.getElementById(SECTION_ID);
      if (el) {
        fire(el);
        return;
      }
      if (attempts < maxAttempts) {
        pollId = window.setTimeout(poll, 200);
      }
    };

    pollId = window.setTimeout(poll, 50);

    return () => {
      cancelled = true;
      if (pollId) window.clearTimeout(pollId);
      if (reapplyId1) window.clearTimeout(reapplyId1);
      if (reapplyId2) window.clearTimeout(reapplyId2);
      if (clearId) window.clearTimeout(clearId);
    };
  }, [searchParams, activeTab, setSearchParams]);

  /** Deep-link from Capacity scoring breakdown (?tab=project&section=capacity-scoring):
   *  Scroll to the Capacity Scoring panel and pulse highlight.
   *  Same pattern as estimation-target deep-link. */
  useEffect(() => {
    const section = searchParams.get('section');
    if (section !== 'capacity-scoring' || activeTab !== 'integrations') return;

    let cancelled = false;
    let pollId: number | undefined;
    let reapplyId1: number | undefined;
    let reapplyId2: number | undefined;
    let clearId: number | undefined;
    let attempts = 0;
    const maxAttempts = 60;

    const HEADER_OFFSET = 88;
    const SECTION_ID = 'profile-capacity-scoring';

    const scrollToSection = (el: HTMLElement) => {
      const rect = el.getBoundingClientRect();
      const target = (window.scrollY || window.pageYOffset || 0) + rect.top - HEADER_OFFSET;
      const scrollEl =
        (document.scrollingElement as HTMLElement | null) || document.documentElement;
      scrollEl.scrollTo({ top: Math.max(0, target), left: 0, behavior: 'auto' });
    };

    const fire = (el: HTMLElement) => {
      scrollToSection(el);
      setHighlightCapacityScoringSection(true);
      reapplyId1 = window.setTimeout(() => scrollToSection(el), 100);
      reapplyId2 = window.setTimeout(() => scrollToSection(el), 400);
      clearId = window.setTimeout(() => {
        setHighlightCapacityScoringSection(false);
      }, 8000);
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('section');
        window.history.replaceState({}, '', url.toString());
      } catch {
        /* noop */
      }
    };

    const poll = () => {
      if (cancelled) return;
      attempts++;
      const el = document.getElementById(SECTION_ID);
      if (el) {
        fire(el);
        return;
      }
      if (attempts < maxAttempts) {
        pollId = window.setTimeout(poll, 200);
      }
    };

    pollId = window.setTimeout(poll, 50);

    return () => {
      cancelled = true;
      if (pollId) window.clearTimeout(pollId);
      if (reapplyId1) window.clearTimeout(reapplyId1);
      if (reapplyId2) window.clearTimeout(reapplyId2);
      if (clearId) window.clearTimeout(clearId);
    };
  }, [searchParams, activeTab, setSearchParams]);

  /** Deep-link from Forecast Step 2 tour (?tab=project&section=forecast-custom-fields):
   *  - Switch tab to project automatically (handled by the generic tab effect via ?tab=project).
   *  - Scroll to and indigo-pulse the Forecast custom fields card-section so the user immediately
   *    sees where the per-product toggles live.
   *  - Same hardening as the distribution-criteria handler below: poll for the element, re-apply
   *    the highlight class a few times to survive re-renders, and strip the section query param. */
  useEffect(() => {
    if (activeTab !== 'integrations') return;
    const initialSection = new URLSearchParams(window.location.search).get('section');
    if (initialSection !== 'forecast-custom-fields') return;

    let cancelled = false;
    let pollId: number | undefined;
    let reapplyId1: number | undefined;
    let reapplyId2: number | undefined;
    let reapplyId3: number | undefined;
    let clearId: number | undefined;
    let attempts = 0;
    const maxAttempts = 60;

    const HIGHLIGHT_MS = 8000;
    const SECTION_ID = 'profile-forecast-custom-fields';
    const HIGHLIGHT_CLS = 'profile-forecast-custom-fields-highlight';

    const addClass = () => {
      document.getElementById(SECTION_ID)?.classList.add(HIGHLIGHT_CLS);
    };
    const removeClass = () => {
      document.getElementById(SECTION_ID)?.classList.remove(HIGHLIGHT_CLS);
    };

    const scrollToSection = (el: HTMLElement) => {
      const headerOffset = 88;
      const rect = el.getBoundingClientRect();
      const target = (window.scrollY || window.pageYOffset || 0) + rect.top - headerOffset;
      const scrollEl =
        (document.scrollingElement as HTMLElement | null) || document.documentElement;
      scrollEl.scrollTo({ top: Math.max(0, target), left: 0, behavior: 'auto' });
    };

    const fire = (el: HTMLElement) => {
      scrollToSection(el);
      setHighlightForecastCustomFieldsSection(true);
      addClass();
      reapplyId1 = window.setTimeout(addClass, 100);
      reapplyId2 = window.setTimeout(addClass, 400);
      reapplyId3 = window.setTimeout(addClass, 1000);
      clearId = window.setTimeout(() => {
        setHighlightForecastCustomFieldsSection(false);
        removeClass();
      }, HIGHLIGHT_MS);
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('section');
        window.history.replaceState({}, '', url.toString());
      } catch {
        /* noop */
      }
    };

    const tick = () => {
      if (cancelled) return;
      const el = document.getElementById(SECTION_ID) as HTMLElement | null;
      if (el) {
        fire(el);
        return;
      }
      attempts += 1;
      if (attempts < maxAttempts) {
        pollId = window.setTimeout(tick, 50);
      }
    };

    pollId = window.setTimeout(tick, 60);

    return () => {
      cancelled = true;
      if (pollId) window.clearTimeout(pollId);
      if (reapplyId1) window.clearTimeout(reapplyId1);
      if (reapplyId2) window.clearTimeout(reapplyId2);
      if (reapplyId3) window.clearTimeout(reapplyId3);
      if (clearId) window.clearTimeout(clearId);
    };
    // We intentionally only re-run on activeTab changes; we read the URL once via window.location.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  /** Deep-link from the Forecast Sprint/Hybrid empty-state hint
   *  (?tab=project&section=jira-role-fields): scroll to and indigo-pulse the
   *  "Jira role fields" card so the user lands exactly on the Developer/QA
   *  pickers that fix the empty swimlanes. Same hardening as the
   *  forecast-custom-fields handler above. */
  useEffect(() => {
    if (activeTab !== 'integrations') return;
    const initialSection = new URLSearchParams(window.location.search).get('section');
    if (initialSection !== 'jira-role-fields') return;

    let cancelled = false;
    let pollId: number | undefined;
    let reapplyId1: number | undefined;
    let reapplyId2: number | undefined;
    let reapplyId3: number | undefined;
    let clearId: number | undefined;
    let attempts = 0;
    const maxAttempts = 60;

    const HIGHLIGHT_MS = 8000;
    const SECTION_ID = 'profile-jira-role-fields';
    const HIGHLIGHT_CLS = 'profile-forecast-custom-fields-highlight';

    const addClass = () => {
      document.getElementById(SECTION_ID)?.classList.add(HIGHLIGHT_CLS);
    };
    const removeClass = () => {
      document.getElementById(SECTION_ID)?.classList.remove(HIGHLIGHT_CLS);
    };

    const scrollToSection = (el: HTMLElement) => {
      const headerOffset = 88;
      const rect = el.getBoundingClientRect();
      const target = (window.scrollY || window.pageYOffset || 0) + rect.top - headerOffset;
      const scrollEl =
        (document.scrollingElement as HTMLElement | null) || document.documentElement;
      scrollEl.scrollTo({ top: Math.max(0, target), left: 0, behavior: 'auto' });
    };

    const fire = (el: HTMLElement) => {
      scrollToSection(el);
      setHighlightJiraRoleFieldsSection(true);
      addClass();
      reapplyId1 = window.setTimeout(addClass, 100);
      reapplyId2 = window.setTimeout(addClass, 400);
      reapplyId3 = window.setTimeout(addClass, 1000);
      clearId = window.setTimeout(() => {
        setHighlightJiraRoleFieldsSection(false);
        removeClass();
      }, HIGHLIGHT_MS);
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('section');
        window.history.replaceState({}, '', url.toString());
      } catch {
        /* noop */
      }
    };

    const tick = () => {
      if (cancelled) return;
      const el = document.getElementById(SECTION_ID) as HTMLElement | null;
      if (el) {
        fire(el);
        return;
      }
      attempts += 1;
      if (attempts < maxAttempts) {
        pollId = window.setTimeout(tick, 50);
      }
    };

    pollId = window.setTimeout(tick, 60);

    return () => {
      cancelled = true;
      if (pollId) window.clearTimeout(pollId);
      if (reapplyId1) window.clearTimeout(reapplyId1);
      if (reapplyId2) window.clearTimeout(reapplyId2);
      if (reapplyId3) window.clearTimeout(reapplyId3);
      if (clearId) window.clearTimeout(clearId);
    };
    // We intentionally only re-run on activeTab changes; we read the URL once via window.location.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  /** Deep-link from Create Commitment modal (?tab=project&section=commitment-scope-types|commitment-delivery-types). */
  useEffect(() => {
    if (activeTab !== 'integrations') return;
    const initialSection = new URLSearchParams(window.location.search).get('section');
    if (
      initialSection !== 'commitment-scope-types' &&
      initialSection !== 'commitment-delivery-types'
    )
      return;

    let cancelled = false;
    let pollId: number | undefined;
    let reapplyId1: number | undefined;
    let reapplyId2: number | undefined;
    let reapplyId3: number | undefined;
    let clearId: number | undefined;
    let attempts = 0;
    const maxAttempts = 60;

    const HIGHLIGHT_MS = 10000;
    const SECTION_ID =
      initialSection === 'commitment-scope-types'
        ? 'settings-commitment-scope-types'
        : 'settings-commitment-delivery-types';
    const HIGHLIGHT_CLS =
      initialSection === 'commitment-scope-types'
        ? 'settings-commitment-scope-highlight'
        : 'settings-commitment-delivery-highlight';

    const addClass = () => {
      document.getElementById(SECTION_ID)?.classList.add(HIGHLIGHT_CLS);
    };
    const removeClass = () => {
      document.getElementById(SECTION_ID)?.classList.remove(HIGHLIGHT_CLS);
    };

    const scrollToSection = (el: HTMLElement) => {
      const headerOffset = 88;
      const rect = el.getBoundingClientRect();
      const target = (window.scrollY || window.pageYOffset || 0) + rect.top - headerOffset;
      const scrollEl =
        (document.scrollingElement as HTMLElement | null) || document.documentElement;
      scrollEl.scrollTo({ top: Math.max(0, target), left: 0, behavior: 'auto' });
    };

    const fire = (el: HTMLElement) => {
      scrollToSection(el);
      addClass();
      reapplyId1 = window.setTimeout(addClass, 100);
      reapplyId2 = window.setTimeout(addClass, 400);
      reapplyId3 = window.setTimeout(addClass, 1000);
      clearId = window.setTimeout(removeClass, HIGHLIGHT_MS);
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('section');
        window.history.replaceState({}, '', url.toString());
      } catch {
        /* noop */
      }
    };

    const tick = () => {
      if (cancelled) return;
      const el = document.getElementById(SECTION_ID) as HTMLElement | null;
      if (el) {
        fire(el);
        return;
      }
      attempts += 1;
      if (attempts < maxAttempts) {
        pollId = window.setTimeout(tick, 50);
      }
    };

    pollId = window.setTimeout(tick, 60);

    return () => {
      cancelled = true;
      if (pollId) window.clearTimeout(pollId);
      if (reapplyId1) window.clearTimeout(reapplyId1);
      if (reapplyId2) window.clearTimeout(reapplyId2);
      if (reapplyId3) window.clearTimeout(reapplyId3);
      if (clearId) window.clearTimeout(clearId);
      removeClass();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  /** Deep-link from Forecast "Roles included in distribution" filter
   *  (?tab=team&section=team-add-member):
   *  - Scroll to and indigo-pulse the Add Team Member subsection.
   *  - Also pulse the Role select, the "+" Add new role button, and the submit Add button so
   *    the user can immediately see the controls they came here for.
   *  - Same hardening pattern as the other deep-link handlers (poll + re-apply + URL cleanup). */
  useEffect(() => {
    if (activeTab !== 'team' || !canManageOrgTeam) return;
    const initialSection = new URLSearchParams(window.location.search).get('section');
    if (initialSection !== 'team-add-member') return;

    let cancelled = false;
    let pollId: number | undefined;
    let reapplyId1: number | undefined;
    let reapplyId2: number | undefined;
    let reapplyId3: number | undefined;
    let clearId: number | undefined;
    let attempts = 0;
    const maxAttempts = 60;

    const HIGHLIGHT_MS = 10000;
    const SECTION_ID = 'profile-team-add-member';
    const SECTION_CLS = 'profile-team-add-member-highlight-section';
    const CONTROL_CLS = 'profile-team-add-member-highlight-control';
    const CONTROL_IDS = [
      'profile-team-add-member-role-select',
      'profile-team-add-member-new-role-btn',
      'profile-team-add-member-submit-btn',
    ];

    const addClasses = () => {
      document.getElementById(SECTION_ID)?.classList.add(SECTION_CLS);
      for (const id of CONTROL_IDS) {
        document.getElementById(id)?.classList.add(CONTROL_CLS);
      }
    };
    const removeClasses = () => {
      document.getElementById(SECTION_ID)?.classList.remove(SECTION_CLS);
      for (const id of CONTROL_IDS) {
        document.getElementById(id)?.classList.remove(CONTROL_CLS);
      }
    };

    const scrollToSection = (el: HTMLElement) => {
      const headerOffset = 88;
      const rect = el.getBoundingClientRect();
      const target = (window.scrollY || window.pageYOffset || 0) + rect.top - headerOffset;
      const scrollEl =
        (document.scrollingElement as HTMLElement | null) || document.documentElement;
      try {
        scrollEl.scrollTo({ top: Math.max(target, 0), behavior: 'smooth' });
      } catch {
        scrollEl.scrollTop = Math.max(target, 0);
      }
    };

    const fire = (el: HTMLElement) => {
      if (cancelled) return;
      scrollToSection(el);
      setHighlightTeamAddMemberSection(true);
      addClasses();
      reapplyId1 = window.setTimeout(addClasses, 100);
      reapplyId2 = window.setTimeout(addClasses, 400);
      reapplyId3 = window.setTimeout(addClasses, 1000);
      clearId = window.setTimeout(() => {
        setHighlightTeamAddMemberSection(false);
        removeClasses();
      }, HIGHLIGHT_MS);
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('section');
        window.history.replaceState({}, '', url.toString());
      } catch {
        /* noop */
      }
    };

    const tick = () => {
      if (cancelled) return;
      const el = document.getElementById(SECTION_ID) as HTMLElement | null;
      if (el) {
        fire(el);
        return;
      }
      attempts += 1;
      if (attempts < maxAttempts) {
        pollId = window.setTimeout(tick, 50);
      }
    };

    pollId = window.setTimeout(tick, 60);

    return () => {
      cancelled = true;
      if (pollId) window.clearTimeout(pollId);
      if (reapplyId1) window.clearTimeout(reapplyId1);
      if (reapplyId2) window.clearTimeout(reapplyId2);
      if (reapplyId3) window.clearTimeout(reapplyId3);
      if (clearId) window.clearTimeout(clearId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, canManageOrgTeam]);

  /** Deep-link from Forecast (?tab=team&section=distribution-criteria):
   *  - Jump to the Distribution criteria block instantly (no smooth scroll animation).
   *  - Pulse it with an indigo halo for ~8s so the user immediately sees where they landed.
   *
   *  Hardened version (mirrors the team-roster deep-link below):
   *  - Reads the URL **once** from `window.location` so re-renders triggered by removing the
   *    `section` query param don't restart this effect (which previously wiped the highlight
   *    immediately via the cleanup function).
   *  - Polls `document.getElementById('profile-distribution-criteria')` for up to ~3s — the
   *    team tab might mount slightly after this effect (lazy data, suspended children, etc.).
   *  - Adds the highlight via direct DOM (independent of React state / className races) and
   *    re-applies it on a timer in case any re-render strips it.
   *  - Holds the highlight for 8s, then removes the class. */
  useEffect(() => {
    if (activeTab !== 'team' || !canManageOrgTeam) return;
    const initialSection = new URLSearchParams(window.location.search).get('section');
    if (initialSection !== 'distribution-criteria') return;

    let cancelled = false;
    let pollId: number | undefined;
    let reapplyId1: number | undefined;
    let reapplyId2: number | undefined;
    let reapplyId3: number | undefined;
    let clearId: number | undefined;
    let attempts = 0;
    const maxAttempts = 60; // ~3s at 50ms

    const HIGHLIGHT_MS = 8000;
    const SECTION_ID = 'profile-distribution-criteria';
    const HIGHLIGHT_CLS = 'profile-distribution-criteria-highlight';

    const addClass = () => {
      document.getElementById(SECTION_ID)?.classList.add(HIGHLIGHT_CLS);
    };

    const removeClass = () => {
      document.getElementById(SECTION_ID)?.classList.remove(HIGHLIGHT_CLS);
    };

    const scrollToSection = (el: HTMLElement) => {
      // Account for sticky `.app-header` (~72px) so the title isn't hidden behind it.
      const headerOffset = 88;
      const rect = el.getBoundingClientRect();
      const target = (window.scrollY || window.pageYOffset || 0) + rect.top - headerOffset;
      const scrollEl =
        (document.scrollingElement as HTMLElement | null) || document.documentElement;
      // Instant jump — no smooth animation.
      scrollEl.scrollTo({ top: Math.max(0, target), left: 0, behavior: 'auto' });
    };

    const fire = (el: HTMLElement) => {
      scrollToSection(el);
      // Reflect highlight in React state too (for the className prop), but rely on
      // direct DOM as the source of truth — re-applied a couple of times below to
      // survive any re-render that may strip the class temporarily.
      setHighlightDistributionCriteriaSection(true);
      addClass();
      reapplyId1 = window.setTimeout(addClass, 100);
      reapplyId2 = window.setTimeout(addClass, 400);
      reapplyId3 = window.setTimeout(addClass, 1000);
      clearId = window.setTimeout(() => {
        setHighlightDistributionCriteriaSection(false);
        removeClass();
      }, HIGHLIGHT_MS);
      // Strip the `section` query param so reload won't re-trigger the deep-link.
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('section');
        window.history.replaceState({}, '', url.toString());
      } catch {
        /* noop */
      }
    };

    const tick = () => {
      if (cancelled) return;
      const el = document.getElementById(SECTION_ID) as HTMLElement | null;
      if (el) {
        fire(el);
        return;
      }
      attempts += 1;
      if (attempts < maxAttempts) {
        pollId = window.setTimeout(tick, 50);
      }
    };

    pollId = window.setTimeout(tick, 60);

    return () => {
      cancelled = true;
      if (pollId) window.clearTimeout(pollId);
      if (reapplyId1) window.clearTimeout(reapplyId1);
      if (reapplyId2) window.clearTimeout(reapplyId2);
      if (reapplyId3) window.clearTimeout(reapplyId3);
      if (clearId) window.clearTimeout(clearId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, canManageOrgTeam]);

  /** Deep-link (?tab=team&section=team-roster): scroll to Team roster + neon-pulse highlight.
   *
   * Hardened version:
   *  - Reads the URL **once** from `window.location` so re-renders triggered by removing the
   *    `section` query param don't restart this effect.
   *  - Polls `document.getElementById('profile-team-roster')` for up to ~3s — the team tab
   *    might mount slightly after this effect (lazy data, suspended children, etc.).
   *  - Scrolls the **window** (document.scrollingElement) explicitly instead of relying on
   *    `scrollIntoView`, which can target a non-window scroll container in some layouts.
   *  - Applies highlight classes via direct DOM (independent of React state / className races)
   *    and re-applies them on a timer in case any re-render strips them.
   *  - Holds the highlight for 12s, then removes both classes and React state. */
  useEffect(() => {
    if (activeTab !== 'team' || !canManageOrgTeam) return;
    const initialSection = new URLSearchParams(window.location.search).get('section');
    if (initialSection !== 'team-roster') return;

    let cancelled = false;
    let pollId: number | undefined;
    let reapplyId1: number | undefined;
    let reapplyId2: number | undefined;
    let reapplyId3: number | undefined;
    let clearId: number | undefined;
    let attempts = 0;
    const maxAttempts = 60; // ~3s at 50ms

    const HIGHLIGHT_MS = 12000;

    const ROSTER_ID = 'profile-team-roster';
    const ROLE_ID = 'profile-team-roster-role-header';

    const SECTION_CLS = 'profile-team-roster-highlight-section';
    const ROLE_CLS = 'profile-team-roster-highlight-role';

    const addClasses = () => {
      document.getElementById(ROSTER_ID)?.classList.add(SECTION_CLS);
      document.getElementById(ROLE_ID)?.classList.add(ROLE_CLS);
    };

    const removeClasses = () => {
      document.getElementById(ROSTER_ID)?.classList.remove(SECTION_CLS);
      document.getElementById(ROLE_ID)?.classList.remove(ROLE_CLS);
    };

    const scrollToSection = (el: HTMLElement) => {
      // Account for sticky `.app-header` (~72px) so the title isn't hidden behind it.
      const headerOffset = 88;
      const rect = el.getBoundingClientRect();
      const target = (window.scrollY || window.pageYOffset || 0) + rect.top - headerOffset;
      const scrollEl =
        (document.scrollingElement as HTMLElement | null) || document.documentElement;
      try {
        scrollEl.scrollTo({ top: Math.max(target, 0), behavior: 'smooth' });
      } catch {
        scrollEl.scrollTop = Math.max(target, 0);
      }
    };

    const fire = (el: HTMLElement) => {
      if (cancelled) return;
      scrollToSection(el);
      setHighlightTeamRosterSection(true);
      addClasses();
      // Re-apply across a few ticks in case any re-render strips conditional classes.
      reapplyId1 = window.setTimeout(addClasses, 60);
      reapplyId2 = window.setTimeout(addClasses, 250);
      reapplyId3 = window.setTimeout(addClasses, 700);
      // Strip the `section` param from URL so reload/back doesn't re-pulse forever.
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('section');
        window.history.replaceState(window.history.state, '', url.toString());
      } catch {
        /* noop */
      }
      clearId = window.setTimeout(() => {
        removeClasses();
        setHighlightTeamRosterSection(false);
      }, HIGHLIGHT_MS);
    };

    const tick = () => {
      if (cancelled) return;
      const el = document.getElementById(ROSTER_ID) as HTMLElement | null;
      if (el) {
        fire(el);
        return;
      }
      attempts += 1;
      if (attempts < maxAttempts) {
        pollId = window.setTimeout(tick, 50);
      }
    };

    pollId = window.setTimeout(tick, 60);

    return () => {
      cancelled = true;
      if (pollId) window.clearTimeout(pollId);
      if (reapplyId1) window.clearTimeout(reapplyId1);
      if (reapplyId2) window.clearTimeout(reapplyId2);
      if (reapplyId3) window.clearTimeout(reapplyId3);
      if (clearId) window.clearTimeout(clearId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, canManageOrgTeam]);

  useEffect(() => {
    if (activeTab !== 'billing' || !canViewBilling || !currentOrg?.id) return;
    let cancelled = false;
    setBillingInvoicesLoading(true);
    void profileApiFetch('/api/billing/invoices')
      .then((res) => res.json())
      .then((data: { invoices?: typeof billingInvoices }) => {
        if (!cancelled) setBillingInvoices(data.invoices || []);
      })
      .catch(() => {
        if (!cancelled) setBillingInvoices([]);
      })
      .finally(() => {
        if (!cancelled) setBillingInvoicesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, canViewBilling, currentOrg?.id, profileApiFetch]);

  const checkoutSuccessParam = searchParams.get('checkout');
  const checkoutSessionIdParam = searchParams.get('session_id');

  /** After Stripe Checkout, webhooks often miss localhost — pull subscription + invoices from Stripe API.
   *  Use primitive query params in deps (not the whole `URLSearchParams` ref) + sessionStorage inflight
   *  so rerenders do not spawn duplicate POSTs. */
  useEffect(() => {
    if (checkoutSuccessParam !== 'success') return;
    if (!canViewBilling || !canModifyBilling || !currentOrg?.id) return;

    const sessionId = checkoutSessionIdParam;
    const storageKey = `wb_billing_sync_done_${currentOrg.id}_${sessionId || 'nosession'}`;
    const inflightKey = `${storageKey}_inflight`;

    if (typeof sessionStorage !== 'undefined') {
      if (sessionStorage.getItem(storageKey)) {
        const next = new URLSearchParams(
          typeof window !== 'undefined' ? window.location.search : ''
        );
        next.delete('checkout');
        next.delete('session_id');
        setSearchParams(next, { replace: true });
        return;
      }
      if (sessionStorage.getItem(inflightKey)) return;
      sessionStorage.setItem(inflightKey, '1');
    }

    let cancelled = false;
    (async () => {
      setBillingStripeSyncing(true);
      try {
        let sessionOk = false;
        if (sessionId) {
          const res = await profileApiFetch('/api/billing/sync-checkout-session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId }),
          });
          sessionOk = res.ok;
        }
        if (!sessionOk) {
          await profileApiFetch('/api/billing/sync-subscription', { method: 'POST' });
        }
        await refreshSubscription();
        const invRes = await profileApiFetch('/api/billing/invoices');
        if (invRes.ok) {
          const data = (await invRes.json()) as { invoices?: typeof billingInvoices };
          if (!cancelled) setBillingInvoices(data.invoices || []);
        }
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem(storageKey, '1');
        }
      } catch {
        /* user can use Refresh status */
      } finally {
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.removeItem(inflightKey);
        }
        const next = new URLSearchParams(
          typeof window !== 'undefined' ? window.location.search : ''
        );
        next.delete('checkout');
        next.delete('session_id');
        setSearchParams(next, { replace: true });
        setBillingStripeSyncing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    checkoutSuccessParam,
    checkoutSessionIdParam,
    canViewBilling,
    canModifyBilling,
    currentOrg?.id,
    profileApiFetch,
    refreshSubscription,
    setSearchParams,
  ]);

  useEffect(() => {
    const dn = currentOrg?.display_name || currentOrg?.name || '';
    setOrgDisplayName(dn);
  }, [currentOrg?.id, currentOrg?.display_name, currentOrg?.name]);

  const visibleProductsSettings = useMemo(
    (): Product[] => getVisibleProductsForOrgUser<Product>(currentOrg),
    [currentOrg]
  );

  const selectedSettingsProduct = useMemo(() => {
    if (!settingsProductId) return null;
    return visibleProductsSettings.find((p) => p.id === settingsProductId) ?? null;
  }, [visibleProductsSettings, settingsProductId]);

  useEffect(() => {
    if (!visibleProductsSettings.length) {
      setSettingsProductId(null);
      return;
    }
    setSettingsProductId((prev) => {
      if (prev && visibleProductsSettings.some((p) => p.id === prev)) return prev;
      const cur =
        currentProduct && visibleProductsSettings.some((p) => p.id === currentProduct.id)
          ? currentProduct.id
          : visibleProductsSettings[0].id;
      return cur;
    });
  }, [visibleProductsSettings, currentProduct?.id]);

  useEffect(() => {
    const p = selectedSettingsProduct;
    if (!p) return;
    setProductDraft({
      name: p.name ?? '',
      jira_project_key: p.jira_project_key ?? '',
      sharepoint_calendar_name: p.sharepoint_calendar_name ?? '',
      sharepoint_calendar_url: p.sharepoint_calendar_url ?? '',
      sharepoint_list_id: p.sharepoint_list_id ?? '',
    });
    setProductIntegrationError(null);
  }, [
    selectedSettingsProduct?.id,
    selectedSettingsProduct?.name,
    selectedSettingsProduct?.jira_project_key,
    selectedSettingsProduct?.sharepoint_calendar_name,
    selectedSettingsProduct?.sharepoint_calendar_url,
    selectedSettingsProduct?.sharepoint_list_id,
  ]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        openCriteriaDropdown &&
        criteriaDropdownRef.current &&
        !criteriaDropdownRef.current.contains(e.target as Node)
      ) {
        setOpenCriteriaDropdown(null);
        setCriteriaSearchQueries((prev) => {
          const next = { ...prev };
          delete next[openCriteriaDropdown];
          return next;
        });
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openCriteriaDropdown]);

  useEffect(() => {
    if (activeTab !== 'team' || !canManageOrgTeam) return;
    profileApiFetch('/api/forecast/meta')
      .then((r) => r.json())
      .then(
        (data: {
          components?: string[];
          labels?: string[];
          customFields?: Array<{
            orgFieldId: number;
            jiraFieldId: string;
            displayName: string;
            inDistribution?: boolean;
            distinctValues?: string[];
          }>;
        }) => {
          // Only surface fields the active product has flagged as
          // `inDistribution=true` — that's the toggle that lights up the
          // dropdown for this member-criteria block.
          const distributionFields = Array.isArray(data.customFields)
            ? data.customFields
                .filter((f) => f && f.inDistribution === true)
                .map((f) => ({
                  orgFieldId: f.orgFieldId,
                  jiraFieldId: f.jiraFieldId,
                  displayName: f.displayName,
                  distinctValues: Array.isArray(f.distinctValues) ? f.distinctValues : [],
                }))
            : [];
          setCriteriaOptions((prev) => ({
            ...prev,
            components: Array.isArray(data.components) ? data.components : prev.components,
            labels: Array.isArray(data.labels) ? data.labels : prev.labels,
            customFields: distributionFields,
          }));
        }
      )
      .catch(() => {});
  }, [activeTab, profileApiFetch, canManageOrgTeam]);

  useEffect(() => {
    const supportStaff = isSupportUser === true;

    if (supportStaff) {
      // Do not hydrate from localStorage or org Jira `email` — avoids mixing customer Atlassian email into the UI.
      // Auth email is always the signed-in support user and is safe to use for Jira Basic Auth when saving org credentials.
      setProfile((prev) => ({
        ...prev,
        name: supportStaffPersonalName(authProfile?.display_name, user?.email),
        email: user?.email?.trim() || prev.email,
      }));
    } else {
      const savedProfile = localStorage.getItem('userProfile');
      if (savedProfile) {
        const profileData = JSON.parse(savedProfile);
        setProfile((prev) => ({
          ...prev,
          ...profileData,
          name:
            profileData.name ||
            authProfile?.display_name ||
            (user?.user_metadata?.full_name as string | undefined) ||
            prev.name,
          email: profileData.email || user?.email || prev.email,
        }));
      } else {
        setProfile((prev) => ({
          ...prev,
          name:
            authProfile?.display_name ||
            (user?.user_metadata?.full_name as string | undefined) ||
            prev.name,
          email: user?.email || prev.email,
        }));
      }
    }

    if (canConfigureIntegrations) {
      // Load Jira credentials from server (survives logout when localStorage was cleared)
      profileApiFetch('/api/jira-credentials')
        .then(async (res) => {
          if (!res.ok) return;
          const config = await res.json();
          if (config && (config.email || config.token || config.project)) {
            setProfile((prev) => ({
              ...prev,
              // My Profile "email" for support staff is always the signed-in Auth user (shown separately in UI).
              ...(!supportStaff ? { email: config.email || prev.email } : {}),
              jiraToken: config.token || prev.jiraToken,
              jiraProject: config.project || prev.jiraProject,
              jiraDomain: config.domain || prev.jiraDomain,
            }));
          }
        })
        .catch(() => {});

      // Загружаем SharePoint настройки из базы данных
      profileApiFetch('/api/sharepoint/config')
        .then(async (res) => {
          if (!res.ok) return;
          const config = await res.json();
          if (config && typeof config === 'object') {
            setProfile((prev) => ({
              ...prev,
              sharePointSiteUrl: config.siteUrl || prev.sharePointSiteUrl,
              sharePointListId: config.listId || prev.sharePointListId,
              sharePointViewId: config.viewId || prev.sharePointViewId,
              sharePointRootFolder: config.rootFolder || prev.sharePointRootFolder,
              sharePointApiKey: config.apiKey || prev.sharePointApiKey,
              sharePointClientId: config.clientId || prev.sharePointClientId,
              sharePointTenantId: config.tenantId || prev.sharePointTenantId,
              sharePointObjectId: config.objectId || prev.sharePointObjectId,
              sharePointClientSecret: config.clientSecret || prev.sharePointClientSecret,
            }));
          }
        })
        .catch(() => {
          console.log('SharePoint config not loaded from database, using defaults');
        });
    }
  }, [
    authProfile?.display_name,
    user?.email,
    user?.user_metadata?.full_name,
    currentOrg?.id,
    profileApiFetch,
    isSupportUser,
    canConfigureIntegrations,
  ]);

  /** When switching org or header product context, reload the roster slice for Team tab UX. */
  useEffect(() => {
    void reloadTeamMembers();
  }, [reloadTeamMembers]);

  // Load project settings (Estimation units) when Team Management tab is active
  useEffect(() => {
    if (activeTab !== 'team' || !canManageOrgTeam) return;
    setLoadingProjectSettings(true);
    profileApiFetch('/api/project-settings')
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((data) => {
        setProjectSettings({
          estimationUnit:
            data.estimationUnit === 'story_points_per_day'
              ? 'story_points_per_day'
              : 'hours_per_day',
          estimationHoursPerDay:
            typeof data.estimationHoursPerDay === 'number' && data.estimationHoursPerDay > 0
              ? data.estimationHoursPerDay
              : 8,
          workingDays:
            Array.isArray(data.workingDays) && data.workingDays.length > 0
              ? data.workingDays
              : [1, 2, 3, 4, 5],
        });
      })
      .catch(() => {
        setProjectSettings({
          estimationUnit: 'hours_per_day',
          estimationHoursPerDay: 8,
          workingDays: [1, 2, 3, 4, 5],
        });
      })
      .finally(() => setLoadingProjectSettings(false));
  }, [activeTab, profileApiFetch, canManageOrgTeam]);

  const saveProjectSettings = async () => {
    setSavingProjectSettings(true);
    setProjectSettingsSaveError(null);
    setProjectSettingsSaved(false);
    try {
      const res = await profileApiFetch('/api/project-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          estimationUnit: projectSettings.estimationUnit,
          estimationHoursPerDay: projectSettings.estimationHoursPerDay,
          workingDays: projectSettings.workingDays,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error || 'Failed to save project settings');
      }
      await refreshUser();
      setProjectSettingsSaved(true);
      setTimeout(() => setProjectSettingsSaved(false), 5000);
    } catch (e) {
      console.error('Error saving project settings:', e);
      setProjectSettingsSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingProjectSettings(false);
    }
  };

  // При открытии Manage roles всегда подгружаем роли с сервера, чтобы в серой коробке были все роли
  useEffect(() => {
    if (showManageRoles) {
      loadRoles();
    }
  }, [showManageRoles]);

  const loadRoles = async () => {
    try {
      const response = await profileApiFetch('/api/roles');
      if (response.ok) {
        const data = await response.json();
        const list = data.roles || [];
        const roleNames = list.map((r: { name: string }) => r.name);
        if (roleNames.length > 0) {
          setAvailableRoles(roleNames);
        }
        setRolesList(
          list.map(
            (r: {
              id: number;
              name: string;
              includeInCapacityMapping?: boolean;
              include_in_capacity_mapping?: boolean;
              capacityCategory?: string;
              capacity_category?: string;
            }) => ({
              id: r.id,
              name: r.name,
              includeInCapacityMapping: roleIncludeInCapacityMappingFromApi(r),
              capacityCategory: roleCapacityCategoryFromApi(r),
            })
          )
        );
      }
    } catch (err) {
      console.error('Error loading roles:', err);
    }
  };

  const handleToggleRoleInMapping = async (id: number, checked: boolean) => {
    try {
      const res = await profileApiFetch(`/api/roles/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ includeInCapacityMapping: checked }),
      });
      if (res.ok) {
        setRolesList((prev) =>
          prev.map((r) => (r.id === id ? { ...r, includeInCapacityMapping: checked } : r))
        );
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Failed to update role');
      }
    } catch (err) {
      console.error('Error updating role:', err);
      alert('Failed to update role');
    }
  };

  const handleChangeCapacityCategory = async (id: number, category: string) => {
    try {
      const res = await profileApiFetch(`/api/roles/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ capacityCategory: category }),
      });
      if (res.ok) {
        setRolesList((prev) =>
          prev.map((r) => (r.id === id ? { ...r, capacityCategory: category } : r))
        );
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Failed to update role category');
      }
    } catch (err) {
      console.error('Error updating role category:', err);
      alert('Failed to update role category');
    }
  };

  const handleRenameRole = async (id: number, newName: string) => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    const role = rolesList.find((r) => r.id === id);
    try {
      const res = await profileApiFetch(`/api/roles/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: trimmed,
          includeInCapacityMapping: role ? roleIncludeInCapacityMappingFromApi(role) : true,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setEditingRoleId(null);
        setEditingRoleName('');
        loadRoles();
      } else {
        alert(data.error || 'Failed to rename role');
      }
    } catch (err) {
      console.error('Error renaming role:', err);
      alert('Failed to rename role');
    }
  };

  const handleDeleteRole = async (id: number) => {
    const role = rolesList.find((r) => r.id === id);
    if (
      !role ||
      !window.confirm(
        `Delete role "${role.name}"? Team members with this role must be reassigned first.`
      )
    )
      return;
    try {
      const response = await profileApiFetch(`/api/roles/${id}`, { method: 'DELETE' });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        loadRoles();
        setShowManageRoles(false);
      } else {
        alert(data.error || 'Failed to delete role');
      }
    } catch (err) {
      console.error('Error deleting role:', err);
      alert('Failed to delete role');
    }
  };

  const handleInputChange = (field: keyof ProfileData, value: string) => {
    if (field === 'email' && isSupportUser) return;
    setProfile((prev) => ({
      ...prev,
      [field]: value,
    }));
    setIsSaved(false);
    setSaveError(null);
  };

  const inviteRoleOptions = useMemo(() => {
    // 3-role invite UI: Owner can invite both Administrator and Member; Admin
    // can only invite Member. Legacy 'viewer' option was collapsed into 'member'.
    const opts: { value: 'administrator' | 'member'; label: string }[] = [];
    if (can(effectiveRole, 'members.invite_admin')) {
      opts.push({ value: 'administrator', label: 'Administrator' });
    }
    if (can(effectiveRole, 'members.invite_member')) {
      opts.push({ value: 'member', label: 'Member' });
    }
    return opts;
  }, [effectiveRole]);

  const showInviteSection = inviteRoleOptions.length > 0;

  useEffect(() => {
    const allowed = inviteRoleOptions.map((o) => o.value);
    if (allowed.length > 0 && !allowed.includes(inviteRole)) {
      setInviteRole(allowed[0]);
    }
  }, [inviteRoleOptions, inviteRole]);

  const openRosterInviteForPerson = useCallback(
    (person: Record<string, unknown>) => {
      setRosterInviteError(null);
      setRosterInviteModalPerson(person);
      const email =
        typeof person.email === 'string' && person.email.trim() !== '' ? person.email.trim() : '';
      setRosterInviteEmail(email);
      setRosterInviteRole(inviteRoleOptions[0]?.value ?? 'member');
      setRosterInviteProductIds(activeProductIdsFromPerson(person));
    },
    [inviteRoleOptions]
  );

  const handleSaveOrgDisplayName = async () => {
    if (!currentOrg?.id) {
      setOrgNameSaveError('No organization selected');
      return;
    }
    const trimmed = orgDisplayName.trim();
    if (!trimmed) {
      setOrgNameSaveError('Organization name cannot be empty');
      return;
    }
    if (trimmed.length > 100) {
      setOrgNameSaveError('Organization name is too long');
      return;
    }
    setOrgNameSaving(true);
    setOrgNameSaveError(null);
    setOrgNameSaved(false);
    try {
      const res = await profileApiFetch(`/api/organizations/${currentOrg.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ display_name: trimmed }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error((data as { error?: string }).error || 'Failed to save organization name');
      }
      await refreshUser();
      setOrgNameSaved(true);
      setTimeout(() => setOrgNameSaved(false), 5000);
    } catch (e) {
      setOrgNameSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setOrgNameSaving(false);
    }
  };

  /** Atlassian Basic-auth email: My Profile first, then sign-in email (covers support / OAuth without profile email). */
  const jiraIntegrationEmail = profile.email?.trim() || user?.email?.trim() || '';

  const persistJiraOrgCredentialsOrThrow = async () => {
    if (!jiraIntegrationEmail || !profile.jiraToken || !profile.jiraProject) {
      throw new Error(
        'Enter your Jira API token and default project key. Use the email under My Profile or your sign-in email as the Atlassian account for this token.'
      );
    }
    const jiraResponse = await profileApiFetch('/api/jira-credentials', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: jiraIntegrationEmail,
        token: profile.jiraToken,
        project: profile.jiraProject,
        domain: 'hrsinternational.atlassian.net',
      }),
    });
    if (!jiraResponse.ok) {
      const errorData = await jiraResponse.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error((errorData as { error?: string }).error || 'Failed to save Jira credentials');
    }
  };

  const persistSharePointOrgConfigOrThrow = async () => {
    if (!profile.sharePointSiteUrl?.trim()) {
      throw new Error('Enter SharePoint site URL first.');
    }
    const sharePointResponse = await profileApiFetch('/api/sharepoint/config', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        apiKey: profile.sharePointApiKey || null,
        siteUrl: profile.sharePointSiteUrl,
        listId: profile.sharePointListId || null,
        viewId: profile.sharePointViewId || null,
        rootFolder: profile.sharePointRootFolder || null,
        clientId: profile.sharePointClientId || null,
        tenantId: profile.sharePointTenantId || null,
        objectId: profile.sharePointObjectId || null,
        clientSecret: profile.sharePointClientSecret || null,
      }),
    });
    if (!sharePointResponse.ok) {
      const errorData = await sharePointResponse.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(
        (errorData as { error?: string }).error || 'Failed to save SharePoint config'
      );
    }
  };

  const persistProductIntegrationOrThrow = async () => {
    if (!selectedSettingsProduct || !currentOrg?.id) {
      throw new Error('Select a product first.');
    }
    const trimmedName = productDraft.name.trim();
    if (!trimmedName) {
      throw new Error('Product name is required');
    }
    if (trimmedName.length > 120) {
      throw new Error('Product name is too long (max 120 characters)');
    }
    const res = await profileApiFetch(`/api/products/${selectedSettingsProduct.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: trimmedName,
        jira_project_key: productDraft.jira_project_key.trim()
          ? productDraft.jira_project_key.trim().toUpperCase()
          : null,
        sharepoint_calendar_name: productDraft.sharepoint_calendar_name.trim() || null,
        sharepoint_calendar_url: productDraft.sharepoint_calendar_url.trim() || null,
        sharepoint_list_id: productDraft.sharepoint_list_id.trim() || null,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.status === 409) {
      throw new Error(
        typeof data.error === 'string'
          ? data.error
          : 'A product with this Jira project key already exists'
      );
    }
    if (!res.ok) {
      throw new Error(typeof data.error === 'string' ? data.error : 'Failed to save product');
    }
  };

  const persistOrgDisplayNameIfAllowedOrThrow = async () => {
    if (!canUpdateOrg || !currentOrg?.id) return;
    const trimmed = orgDisplayName.trim();
    if (!trimmed) return;
    if (trimmed.length > 100) {
      throw new Error('Organization name is too long');
    }
    const res = await profileApiFetch(`/api/organizations/${currentOrg.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ display_name: trimmed }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error((data as { error?: string }).error || 'Failed to save organization name');
    }
  };

  const handleSaveJiraSection = async () => {
    setSaveError(null);
    setJiraSectionError(null);
    setJiraSectionSaved(false);
    setJiraSectionSaving(true);
    try {
      await persistJiraOrgCredentialsOrThrow();
      localStorage.setItem('userProfile', JSON.stringify(profile));
      await refreshUser();
      setJiraSectionSaved(true);
      setTimeout(() => setJiraSectionSaved(false), 5000);
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      setJiraSectionError(msg);
    } finally {
      setJiraSectionSaving(false);
    }
  };

  const handleSaveSharePointSection = async () => {
    setSaveError(null);
    setSharePointSectionError(null);
    setSharePointSectionSaved(false);
    setSharePointSectionSaving(true);
    try {
      await persistSharePointOrgConfigOrThrow();
      localStorage.setItem('userProfile', JSON.stringify(profile));
      await refreshUser();
      setSharePointSectionSaved(true);
      setTimeout(() => setSharePointSectionSaved(false), 5000);
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      setSharePointSectionError(msg);
    } finally {
      setSharePointSectionSaving(false);
    }
  };

  const loadOrgRbacMembers = useCallback(async () => {
    if (!currentOrg?.id || currentOrg.role !== 'owner') return;
    setOrgRbacLoading(true);
    setOrgRbacError(null);
    try {
      const res = await profileApiFetch(`/api/organizations/${currentOrg.id}/members/rbac-admin`);
      const data = (await res.json()) as { members?: OrgRbacMemberRow[]; error?: string };
      if (!res.ok) throw new Error(data.error || 'Failed to load organization members');
      const list = data.members ?? [];
      setOrgRbacMembers(list);
      const draft: Record<string, string> = {};
      for (const m of list) {
        draft[m.user_id] = String(m.role || 'member').toLowerCase();
      }
      setOrgRbacRoleDraft(draft);
    } catch (e) {
      setOrgRbacError(e instanceof Error ? e.message : String(e));
    } finally {
      setOrgRbacLoading(false);
    }
  }, [currentOrg?.id, currentOrg?.role, profileApiFetch]);

  useEffect(() => {
    if (activeTab !== 'team' || currentOrg?.role !== 'owner') return;
    void loadOrgRbacMembers();
  }, [activeTab, currentOrg?.role, currentOrg?.id, loadOrgRbacMembers]);

  const executeOrgRbacPatch = async (targetUserId: string, orgRole: string) => {
    if (!currentOrg?.id) return;
    setOrgRbacSavingUserId(targetUserId);
    setOrgRbacError(null);
    try {
      const res = await profileApiFetch(
        `/api/organizations/${currentOrg.id}/members/${targetUserId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ org_role: orgRole }),
        }
      );
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(data.error || 'Failed to update role');
      await refreshUser();
      await loadOrgRbacMembers();
    } catch (e) {
      setOrgRbacError(e instanceof Error ? e.message : String(e));
    } finally {
      setOrgRbacSavingUserId(null);
      setOrgRbacOwnershipConfirm(null);
    }
  };

  const handleOrgRbacSaveRow = (targetUserId: string) => {
    const desired = (orgRbacRoleDraft[targetUserId] || 'member').toLowerCase();
    const currentRow = orgRbacMembers.find((m) => m.user_id === targetUserId);
    const currentRole = String(currentRow?.role || '').toLowerCase();
    if (desired === currentRole) return;
    if (desired === 'owner' && targetUserId !== user?.id) {
      const label =
        (typeof currentRow?.email === 'string' && currentRow.email.trim()) ||
        (typeof currentRow?.display_name === 'string' && currentRow.display_name.trim()) ||
        targetUserId;
      setOrgRbacOwnershipConfirm({ userId: targetUserId, displayLabel: label });
      return;
    }
    void executeOrgRbacPatch(targetUserId, desired);
  };

  const _formatOrgRbacDate = (iso: string | null | undefined) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    } catch {
      return '—';
    }
  };

  const persistPersonProductIds = useCallback(
    async (person: Record<string, unknown>, nextIds: string[]) => {
      const pid = String(person.id ?? '');
      if (!pid) return;
      setRosterProductToggleUserId(pid);
      try {
        const res = await profileApiFetch(`/api/roster/persons/${encodeURIComponent(pid)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ productIds: nextIds }),
        });
        const errBody = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(
            typeof (errBody as { error?: string }).error === 'string'
              ? (errBody as { error?: string }).error
              : 'Update failed'
          );
        }
        await reloadTeamMembers();
      } catch (e) {
        alert(e instanceof Error ? e.message : 'Could not update roster products');
      } finally {
        setRosterProductToggleUserId(null);
      }
    },
    [profileApiFetch, reloadTeamMembers]
  );

  useEffect(() => {
    if (activeTab !== 'team') return;
    const q = (newMember.name || '').trim();
    if (q.length < 2) {
      setAddMemberSearchResults([]);
      return;
    }
    const timer = window.setTimeout(() => {
      void (async () => {
        setAddMemberSearchLoading(true);
        try {
          const res = await profileApiFetch(
            `/api/roster/persons/search?q=${encodeURIComponent(q)}`
          );
          if (!res.ok) return;
          const data = (await res.json()) as { results?: Record<string, unknown>[] };
          setAddMemberSearchResults(data.results || []);
        } finally {
          setAddMemberSearchLoading(false);
        }
      })();
    }, 300);
    return () => window.clearTimeout(timer);
  }, [newMember.name, activeTab, profileApiFetch]);

  const handleInviteOrgMember = async () => {
    setInviteError(null);
    setInviteMessage(null);
    if (!currentOrg?.id) {
      setInviteError('No organization selected');
      return;
    }
    const trimmed = inviteEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setInviteError('Enter a valid email address');
      return;
    }
    const trimmedLower = trimmed.toLowerCase();
    const already = orgRbacMembers.some(
      (m) => (m.email || '').trim().toLowerCase() === trimmedLower
    );
    if (already) {
      setInviteError(
        'Already in organization. Remove the existing member first or use another email.'
      );
      return;
    }
    const apiRole = inviteRole === 'administrator' ? 'administrator' : 'member';
    setInviteLoading(true);
    try {
      const inviteBody: Record<string, unknown> = { email: trimmed, role: apiRole };
      if (inviteOrgProductIds.length > 0) {
        inviteBody.products = inviteOrgProductIds;
      }
      const res = await profileApiFetch(`/api/organizations/${currentOrg.id}/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(inviteBody),
      });
      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        message?: string;
        emailSent?: boolean;
        emailKind?: string | null;
      };
      if (!res.ok) {
        throw new Error(data.error || 'Invite failed');
      }
      const orgLabel =
        currentOrg.display_name?.trim() || currentOrg.name?.trim() || 'this organization';
      let inviteMsg = data.message || 'Invitation processed';
      if (data.emailSent && data.emailKind === 'magic_link') {
        inviteMsg = `We sent ${trimmed} a one-time sign-in link. They’re in ${orgLabel} and can open Relix from that email—ask them to check spam if it doesn’t arrive.`;
      } else if (data.emailSent && data.emailKind === 'signup_invite') {
        inviteMsg = `We sent ${trimmed} a link to finish joining ${orgLabel}. They’re already in this organization; once they create a password they can sign in.`;
      }
      setInviteMessage(inviteMsg);
      setInviteEmail('');
      setInviteOrgProductIds([]);
      await refreshUser();
      void loadOrgRbacMembers();
    } catch (e) {
      setInviteError(e instanceof Error ? e.message : String(e));
    } finally {
      setInviteLoading(false);
    }
  };

  const handleSubmitRosterInvite = async () => {
    if (!rosterInviteModalPerson?.id || !currentOrg?.id) return;
    setRosterInviteError(null);
    const em = rosterInviteEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      setRosterInviteError('Enter a valid email address');
      return;
    }
    setRosterInviteLoading(true);
    try {
      const roleApi = rosterInviteRole === 'administrator' ? 'administrator' : 'member';
      const body: Record<string, unknown> = { email: em, role: roleApi };
      if (rosterInviteProductIds.length > 0) body.products = rosterInviteProductIds;
      const res = await profileApiFetch(
        `/api/roster/persons/${encodeURIComponent(String(rosterInviteModalPerson.id))}/invite`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      );
      const errBody = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          typeof (errBody as { error?: string }).error === 'string'
            ? (errBody as { error?: string }).error
            : 'Invite failed'
        );
      }
      setRosterInviteModalPerson(null);
      setRosterInviteEmail('');
      setRosterInviteProductIds([]);
      await reloadTeamMembers();
      await refreshUser();
      void loadOrgRbacMembers();
    } catch (e) {
      setRosterInviteError(e instanceof Error ? e.message : String(e));
    } finally {
      setRosterInviteLoading(false);
    }
  };

  const executeRemoveOrgMember = async () => {
    if (!currentOrg?.id || !orgMemberRemoveTarget?.user_id) return;
    setOrgMemberRemoveLoading(true);
    try {
      const res = await profileApiFetch(
        `/api/organizations/${currentOrg.id}/members/${encodeURIComponent(orgMemberRemoveTarget.user_id)}`,
        { method: 'DELETE' }
      );
      const errBody = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          typeof (errBody as { error?: string }).error === 'string'
            ? (errBody as { error?: string }).error
            : 'Remove failed'
        );
      }
      setOrgMemberRemoveTarget(null);
      await refreshUser();
      await loadOrgRbacMembers();
      await reloadTeamMembers();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to remove member');
    } finally {
      setOrgMemberRemoveLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    setPasswordSectionError(null);
    setPasswordSectionMessage(null);
    if (!passwordCurrent.trim()) {
      setPasswordSectionError('Enter your current password.');
      return;
    }
    if (passwordNew.length < 6) {
      setPasswordSectionError('New password must be at least 6 characters.');
      return;
    }
    if (passwordNew !== passwordConfirm) {
      setPasswordSectionError('New password and confirmation do not match.');
      return;
    }
    setPasswordChanging(true);
    try {
      const { error } = await changePassword(passwordCurrent, passwordNew);
      if (error) {
        setPasswordSectionError(error);
        return;
      }
      setPasswordSectionMessage('Password updated successfully.');
      setPasswordCurrent('');
      setPasswordNew('');
      setPasswordConfirm('');
    } finally {
      setPasswordChanging(false);
    }
  };

  const handleSendPasswordResetEmail = async () => {
    setPasswordSectionError(null);
    setPasswordSectionMessage(null);
    const email = user?.email?.trim();
    if (!email) {
      setPasswordSectionError(
        'No account email is available. Sign out and use Forgot password on the sign-in page.'
      );
      return;
    }
    setResetEmailSending(true);
    try {
      const { error } = await sendPasswordResetEmail(email);
      if (error) {
        setPasswordSectionError(error);
        return;
      }
      setPasswordSectionMessage(
        'If this email is registered, you will receive a link to set a new password. Check your inbox and spam folder.'
      );
    } finally {
      setResetEmailSending(false);
    }
  };

  const handleSave = async () => {
    if (activeTab === 'billing') {
      return;
    }
    const urlSection = searchParams.get('section') || searchParams.get('tab');
    let tabForSave: 'profile' | 'project' | 'team' = 'profile';
    if (urlSection === 'profile') {
      tabForSave = 'profile';
    } else if (urlSection === 'team') {
      tabForSave = 'team';
    } else if (
      urlSection === 'project' ||
      urlSection === 'integrations' ||
      urlSection === 'organization'
    ) {
      tabForSave = 'project';
    } else if (activeTab === 'integrations') {
      tabForSave = 'project';
    } else if (activeTab === 'team') {
      tabForSave = 'team';
    }

    pushSaveDiag(
      `handleSave start | tabForSave=${tabForSave} activeTab=${activeTab} urlSection=${urlSection ?? 'null'} orgId=${currentOrg?.id || localStorage.getItem('relix_org_id') ? 'yes' : 'NO'} email=${jiraIntegrationEmail ? 'yes' : 'no'} token=${profile.jiraToken ? 'yes' : 'no'} project=${profile.jiraProject || 'empty'}`
    );

    setSaveError(null);
    setIsSaving(true);

    try {
      if (tabForSave === 'profile') {
        const hasIdentityEmail = !!(profile.email?.trim() || user?.email?.trim());
        if (!hasIdentityEmail) {
          pushSaveDiag('validation FAIL: email required (profile tab)');
          setSaveError(
            isSupportUser
              ? 'No account email is available. Sign out and sign in again.'
              : 'Please enter your Email Address'
          );
          return;
        }
        pushSaveDiag('PATCH /api/auth/profile …');
        const profileRes = await profileApiFetch('/api/auth/profile', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ display_name: profile.name }),
        });
        if (!profileRes.ok) {
          const err = await profileRes.json().catch(() => ({}));
          throw new Error((err as { error?: string }).error || 'Failed to save profile');
        }
        localStorage.setItem('userProfile', JSON.stringify(profile));
        pushSaveDiag('profile PATCH OK');
        await refreshUser();
        setIsSaved(true);
        pushSaveDiag('DONE — success banner');
        setTimeout(() => setIsSaved(false), 5000);
        return;
      }

      if (tabForSave === 'project') {
        setJiraSectionError(null);
        setSharePointSectionError(null);
        pushSaveDiag('validation OK — project tab save all');
        if (canConfigureIntegrations) {
          await persistOrgDisplayNameIfAllowedOrThrow();
          pushSaveDiag('org display name OK (if applicable)');
          if (profile.jiraToken && jiraIntegrationEmail && profile.jiraProject) {
            pushSaveDiag('POST /api/jira-credentials …');
            await persistJiraOrgCredentialsOrThrow();
            pushSaveDiag('Jira POST OK');
          } else {
            pushSaveDiag('skip Jira POST (incomplete credentials)');
          }
          if (profile.sharePointSiteUrl) {
            pushSaveDiag('POST /api/sharepoint/config …');
            await persistSharePointOrgConfigOrThrow();
            pushSaveDiag('SharePoint POST OK');
          } else {
            pushSaveDiag('skip SharePoint POST (no site URL)');
          }
          if (selectedSettingsProduct && currentOrg?.id) {
            pushSaveDiag('PATCH /api/products …');
            await persistProductIntegrationOrThrow();
            pushSaveDiag('Product integration PATCH OK');
          } else {
            pushSaveDiag('skip product PATCH (no product selected)');
          }
        } else {
          pushSaveDiag('skip owner-only integration payloads (effective role)');
        }
        localStorage.setItem('userProfile', JSON.stringify(profile));
        pushSaveDiag('localStorage userProfile updated');
        await refreshUser();
        setIsSaved(true);
        pushSaveDiag('DONE — success banner');
        setTimeout(() => setIsSaved(false), 5000);
        return;
      }

      if (tabForSave === 'team') {
        pushSaveDiag('validation OK — team tab');
        if (canConfigureIntegrations) {
          if (profile.jiraToken && jiraIntegrationEmail && profile.jiraProject) {
            pushSaveDiag('POST /api/jira-credentials …');
            await persistJiraOrgCredentialsOrThrow();
            pushSaveDiag('Jira POST OK');
          } else {
            pushSaveDiag('skip Jira POST (incomplete credentials)');
          }
          if (profile.sharePointSiteUrl) {
            pushSaveDiag('POST /api/sharepoint/config …');
            await persistSharePointOrgConfigOrThrow();
            pushSaveDiag('SharePoint POST OK');
          } else {
            pushSaveDiag('skip SharePoint POST (no site URL)');
          }
        } else {
          pushSaveDiag('skip owner-only integration payloads on team tab');
        }
        localStorage.setItem('userProfile', JSON.stringify(profile));
        pushSaveDiag('localStorage userProfile updated');
        await refreshUser();
        setIsSaved(true);
        pushSaveDiag('DONE — success banner');
        setTimeout(() => setIsSaved(false), 5000);
        return;
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      pushSaveDiag(`ERROR: ${msg}`);
      console.error('[ProfilePage.save] failed', error);
      setSaveError(msg || 'Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddTeamMember = async () => {
    if (!newMember.name || newMember.name.trim() === '') {
      alert('Please enter team member name');
      return;
    }

    if (!currentProduct?.id) {
      alert(
        'Select a product first (header). New roster rows are created for the current product.'
      );
      return;
    }

    const resetAfterSuccess = () => {
      setAddMemberRosterPick(null);
      setAddMemberSearchResults([]);
      setNewMember({
        name: '',
        email: '',
        jiraUsername: '',
        capacityHoursPerDay: 8.0,
        includeInCapacity: true,
        role: 'developer',
        includeInHistoricalCapacity: false,
        excludeFromDistribution: false,
        startDateInTeam: undefined,
        endDateInTeam: undefined,
      });
    };

    const patchTeamMemberFromForm = async (personIdStr: string, tmId: number) => {
      const includeInCapacityValue =
        newMember.includeInCapacity === true || newMember.includeInCapacity === undefined;
      await profileApiFetch(`/api/roster/persons/${encodeURIComponent(personIdStr)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          teamMemberPatches: {
            [String(tmId)]: {
              capacityHoursPerDay: newMember.capacityHoursPerDay || 8.0,
              includeInCapacity: includeInCapacityValue,
              isActive: true,
              includeInHistoricalCapacity: newMember.includeInHistoricalCapacity === true,
              excludeFromDistribution: newMember.excludeFromDistribution === true,
              role: newMember.role || 'developer',
              startDateInTeam: newMember.startDateInTeam ?? null,
              endDateInTeam: newMember.endDateInTeam ?? null,
            },
          },
        }),
      });
    };

    try {
      /** Add current product (+ optional patches) when the name matches an existing roster person */
      const pickIdRaw = addMemberRosterPick?.id;
      const pickMatchesName =
        addMemberRosterPick &&
        typeof addMemberRosterPick.display_name === 'string' &&
        addMemberRosterPick.display_name.trim().toLowerCase() ===
          newMember.name.trim().toLowerCase();
      const pickPersonId = pickIdRaw != null && pickMatchesName ? String(pickIdRaw).trim() : '';

      if (pickPersonId) {
        const rosterRes = await profileApiFetch('/api/roster/persons');
        if (!rosterRes.ok) {
          const errBody = await rosterRes.json().catch(() => ({}));
          throw new Error(
            typeof (errBody as { error?: string }).error === 'string'
              ? (errBody as { error?: string }).error
              : 'Failed to load roster'
          );
        }
        const rosterJson = (await rosterRes.json()) as { persons?: Record<string, unknown>[] };
        const persons = rosterJson.persons || [];
        const personAgg = persons.find((p) => String(p.id) === pickPersonId);
        if (!personAgg) throw new Error('Could not refresh that roster person');

        const activeIds = activeProductIdsFromPerson(personAgg);
        const nextIds = activeIds.some((pid) => String(pid) === String(currentProduct.id))
          ? activeIds
          : Array.from(new Set([...activeIds, String(currentProduct.id)]));

        const putRes = await profileApiFetch(
          `/api/roster/persons/${encodeURIComponent(pickPersonId)}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              displayName: newMember.name.trim(),
              email: newMember.email?.trim() || null,
              jiraUsername: newMember.jiraUsername?.trim() || null,
              productIds: nextIds,
            }),
          }
        );
        const errPut = await putRes.json().catch(() => ({}));
        if (!putRes.ok) {
          throw new Error(
            typeof (errPut as { error?: string }).error === 'string'
              ? (errPut as { error?: string }).error
              : 'Update failed'
          );
        }

        const updated = errPut as {
          id?: unknown;
          products?: Array<{ productId?: string; teamMemberId?: number }>;
        };
        const slice = updated.products?.find(
          (pr) => String(pr.productId) === String(currentProduct.id)
        );
        const tmId = slice?.teamMemberId;
        if (updated.id != null && tmId != null) {
          await patchTeamMemberFromForm(String(updated.id), Number(tmId));
        }
        resetAfterSuccess();
        await reloadTeamMembers();
        return;
      }

      const response = await profileApiFetch('/api/roster/persons', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          displayName: newMember.name.trim(),
          email: newMember.email?.trim() || null,
          jiraUsername: newMember.jiraUsername?.trim() || null,
          productIds: [currentProduct.id],
        }),
      });

      if (response.ok) {
        const createdPerson = (await response.json()) as {
          id?: unknown;
          products?: Array<{ teamMemberId?: number; productId?: string }>;
        };
        const tmId =
          typeof createdPerson === 'object' && createdPerson
            ? createdPerson.products?.find(
                (pr) => String(pr.productId) === String(currentProduct.id)
              )?.teamMemberId
            : undefined;
        /** Apply editable defaults that POST does not expose on create */
        if (createdPerson?.id != null && tmId != null) {
          await patchTeamMemberFromForm(String(createdPerson.id), Number(tmId));
        }
        resetAfterSuccess();
        await reloadTeamMembers();
      } else {
        const error = await response.json().catch(() => ({}));
        alert(
          `Failed to add team member: ${(error as { error?: string }).error || 'Unknown error'}`
        );
      }
    } catch (error) {
      console.error('Error adding team member:', error);
      alert(error instanceof Error ? error.message : 'Failed to add team member');
    }
  };

  const handleDeleteTeamMember = async (id: number) => {
    if (!window.confirm('Remove this person from the current product roster?')) {
      return;
    }

    try {
      const row = teamMembers.find((m) => m.id === id);
      if (!row?.personId || !currentProduct?.id) {
        alert('Could not resolve roster identity. Reload the Team tab and try again.');
        return;
      }

      const rosterRes = await profileApiFetch('/api/roster/persons');
      if (!rosterRes.ok) throw new Error('Failed to load roster');
      const rosterJson = await rosterRes.json();
      const persons = (rosterJson.persons || []) as Array<Record<string, unknown>>;
      const person = persons.find((p) => String(p.id) === row.personId);
      if (!person) throw new Error('Person not found in roster');

      const allIdsRaw = Array.isArray(person.productIdsAll) ? person.productIdsAll : [];
      const remainingProductIds = allIdsRaw
        .map((x: unknown) => String(x))
        .filter((pid) => pid !== String(currentProduct.id));

      const putRes = await profileApiFetch(
        `/api/roster/persons/${encodeURIComponent(row.personId)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ productIds: remainingProductIds }),
        }
      );
      const errBody = await putRes.json().catch(() => ({}));
      if (!putRes.ok) {
        throw new Error(
          typeof (errBody as { error?: string }).error === 'string'
            ? (errBody as { error?: string }).error
            : 'Remove failed'
        );
      }
      await reloadTeamMembers();
    } catch (error) {
      console.error('Error removing team member from product:', error);
      alert(error instanceof Error ? error.message : 'Failed to remove team member');
    }
  };

  const handleUpdateTeamMember = async (member: TeamMember) => {
    if (!member.id) return;
    if (!member.personId) {
      throw new Error(
        'This row is missing roster identity — reload the Team tab or refresh the page, then try again.'
      );
    }

    try {
      const includeInCapacityValue =
        member.includeInCapacity === true || member.includeInCapacity === undefined;
      const dcStr = serializeDistributionCriteriaForRoster(member);

      const response = await profileApiFetch(
        `/api/roster/persons/${encodeURIComponent(member.personId)}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            displayName: member.name.trim(),
            email: member.email?.trim() || null,
            jiraUsername: member.jiraUsername?.trim() || null,
            teamMemberPatches: {
              [String(member.id)]: {
                capacityHoursPerDay: member.capacityHoursPerDay ?? 8.0,
                isActive: member.isActive !== false,
                includeInCapacity: includeInCapacityValue,
                role: member.role || 'developer',
                includeInHistoricalCapacity: member.includeInHistoricalCapacity === true,
                excludeFromDistribution: member.excludeFromDistribution === true,
                startDateInTeam: member.startDateInTeam ?? null,
                endDateInTeam: member.endDateInTeam ?? null,
                distributionCriteria: dcStr,
              },
            },
          }),
        }
      );

      if (response.ok) {
        setTeamMembers((prevMembers) =>
          prevMembers.map((m) =>
            m.id === member.id
              ? {
                  ...m,
                  includeInCapacity: includeInCapacityValue,
                  excludeFromDistribution: member.excludeFromDistribution,
                  isActive: member.isActive !== false,
                }
              : m
          )
        );
      } else {
        const error = await response.json().catch(() => ({}));
        await reloadTeamMembers();
        throw new Error(
          `Failed to update team member: ${(error as { error?: string }).error || 'Unknown error'}`
        );
      }
    } catch (error) {
      console.error('Error updating team member:', error);
      await reloadTeamMembers();
      throw error;
    }
  };

  const handleFetchTeamSuggestions = async () => {
    if (!canViewTeam) return;
    setTeamSuggestionsLoading(true);
    setTeamSuggestionsError(null);
    try {
      const res = await profileApiFetch(
        '/api/team-members/suggestions?sinceMonths=12&minTouches=1'
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(typeof data.error === 'string' ? data.error : 'Failed to load suggestions');
      }
      setTeamSuggestionsData(data);
      const sel = new Set<string>(
        (data.candidates || []).map((c: { displayName: string }) => c.displayName)
      );
      setTeamSuggestionSelected(sel);
      setTeamSuggestionProductOverrides({});
      setExpandedSuggestions(new Set());
    } catch (e) {
      setTeamSuggestionsError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setTeamSuggestionsLoading(false);
    }
  };

  const handleBulkAddTeamSuggestions = async () => {
    if (!canModifyTeam || !teamSuggestionsData) return;
    const selected = teamSuggestionsData.candidates.filter((c) =>
      teamSuggestionSelected.has(c.displayName)
    );
    if (selected.length === 0) {
      alert('Select at least one person to add.');
      return;
    }
    setTeamSuggestionsLoading(true);
    try {
      const items = selected.map((c) => {
        const overrides = teamSuggestionProductOverrides[c.displayName];
        const productIds = overrides
          ? overrides
          : Object.keys(c.touchesByProduct || {}).filter(
              (pid) => (c.touchesByProduct[pid] || 0) > 0
            );
        return { displayName: c.displayName, productIds };
      });

      const res = await profileApiFetch('/api/roster/persons/bulk-from-activity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(typeof data.error === 'string' ? data.error : 'Bulk add failed');
      }

      const addedCount = (data.results || []).reduce(
        (sum: number, r: { added?: number }) => sum + (r.added || 0),
        0
      );
      const errorCount = (data.errors || []).length;

      await reloadTeamMembers();
      await handleFetchTeamSuggestions();
      alert(
        `Added ${addedCount} member-product pair(s).` +
          (errorCount > 0 ? ` ${errorCount} error(s).` : '')
      );
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Bulk add failed');
    } finally {
      setTeamSuggestionsLoading(false);
    }
  };

  const handleTestSharePoint = async () => {
    if (!profile.sharePointSiteUrl || !profile.sharePointSiteUrl.trim()) {
      alert('Please enter SharePoint Site URL first');
      return;
    }

    setTestingSharePoint(true);
    setSharePointTestResult(null);

    try {
      // Server uses org credentials from sharepoint_config (saved via Save SharePoint / Save all).
      const response = await profileApiFetch('/api/sharepoint/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      const testResult = await response.json();

      if (testResult.success === true || testResult.connected === true) {
        let message = `✅ ${testResult.message || 'Connection successful via Microsoft Graph API!'} `;
        if (testResult.calendarList) {
          message += `Found calendar list: "${testResult.calendarList.name}". `;
        }
        if (testResult.hasData) {
          message += `Found ${testResult.itemsCount || 0} event(s).`;
        } else {
          message += 'No events found yet (calendar may be empty).';
        }
        setSharePointTestResult(message);
      } else {
        setSharePointTestResult(
          `❌ Connection failed: ${testResult.error || testResult.message || 'Unknown error'}`
        );
      }
    } catch (error) {
      setSharePointTestResult(
        `❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setTestingSharePoint(false);
    }
  };

  const handleSaveProductIntegration = async () => {
    if (!selectedSettingsProduct || !currentOrg?.id) {
      setProductIntegrationError('No product selected');
      return;
    }
    if (!canUpdateProduct) {
      setProductIntegrationError(
        'Only organization owners and administrators can save product settings.'
      );
      return;
    }
    setProductIntegrationError(null);
    setProductIntegrationSaved(false);
    setProductIntegrationSaving(true);
    try {
      await persistProductIntegrationOrThrow();
      await refreshUser();
      setProductIntegrationSaved(true);
      setTimeout(() => setProductIntegrationSaved(false), 4000);
    } catch (e) {
      setProductIntegrationError(e instanceof Error ? e.message : 'Network error');
    } finally {
      setProductIntegrationSaving(false);
    }
  };

  const handleTestProductCalendar = async () => {
    if (!profile.sharePointSiteUrl || !profile.sharePointSiteUrl.trim()) {
      setProductCalendarTestResult('Enter organization SharePoint site URL above first.');
      return;
    }
    // Do not require tenant/client/secret in React state: the test API uses credentials already
    // stored for the org in sharepoint_config (saved via Save SharePoint connection or Save all).
    setTestingProductCalendar(true);
    setProductCalendarTestResult(null);
    try {
      const response = await profileApiFetch('/api/sharepoint/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      let testResult: Record<string, unknown> = {};
      try {
        testResult = (await response.json()) as Record<string, unknown>;
      } catch {
        testResult = {};
      }
      if (!response.ok) {
        const errText =
          (typeof testResult.error === 'string' && testResult.error) ||
          (typeof testResult.message === 'string' && testResult.message) ||
          `HTTP ${response.status}`;
        setProductCalendarTestResult(
          response.status === 403
            ? `Connection check failed: ${errText}. Testing integrations requires owner or administrator.`
            : `Connection check failed: ${errText}`
        );
        return;
      }
      const ok = testResult.success === true || testResult.connected === true;
      if (ok) {
        setProductCalendarTestResult(
          'Azure AD connection OK. This checks org Graph credentials only. Calendar → Sync with SharePoint uses the product selected in the app header; switch there before syncing.'
        );
      } else {
        const errText =
          (typeof testResult.error === 'string' && testResult.error) ||
          (typeof testResult.message === 'string' && testResult.message) ||
          'Unknown error';
        setProductCalendarTestResult(`Connection check failed: ${errText}`);
      }
    } catch (error) {
      setProductCalendarTestResult(
        `Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setTestingProductCalendar(false);
    }
  };

  const handleConfirmDeleteProduct = async () => {
    if (!deleteConfirmProduct || !currentOrg?.id) return;
    setProductDeleteLoading(true);
    try {
      const res = await profileApiFetch(`/api/products/${deleteConfirmProduct.id}`, {
        method: 'DELETE',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(typeof data.error === 'string' ? data.error : 'Failed to delete product');
        return;
      }
      if (currentProduct?.id === deleteConfirmProduct.id) {
        const remaining = visibleProductsSettings.filter((p) => p.id !== deleteConfirmProduct.id);
        if (remaining[0]) selectProduct(remaining[0]);
      }
      setDeleteConfirmProduct(null);
      await refreshUser();
    } catch {
      alert('Network error');
    } finally {
      setProductDeleteLoading(false);
    }
  };

  const productIntegrationConfigured = (p: Product) => {
    const jiraOk = !!(p.jira_project_key && String(p.jira_project_key).trim());
    const spOk = !!(
      (p.sharepoint_calendar_url && p.sharepoint_calendar_url.trim()) ||
      (p.sharepoint_list_id && p.sharepoint_list_id.trim())
    );
    return jiraOk || spOk;
  };

  /** Email shown on My Profile: saved profile email, else sign-in email (same basis as Jira Basic Auth). */
  const personalInfoEmailDisplay = profile.email?.trim() || user?.email?.trim() || '';

  const [settingsSidebarCollapsed, setSettingsSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem('relix_settings_sidebar_collapsed') === '1';
    } catch {
      return false;
    }
  });

  const handleSettingsSidebarCollapsedChange = useCallback((collapsed: boolean) => {
    setSettingsSidebarCollapsed(collapsed);
    try {
      window.localStorage.setItem('relix_settings_sidebar_collapsed', collapsed ? '1' : '0');
    } catch {
      /* ignore quota / privacy mode */
    }
  }, []);

  const handleSectionChange = useCallback(
    (section: SettingsSection) => {
      setActiveTab(section);
      setSearchParams({ section });
      setRosterPage(0);
    },
    [setSearchParams]
  );

  // ── Team tab context value ──────────────────────────────
  const teamContextValue = useMemo<TeamTabContextValue>(
    () => ({
      currentOrg,
      currentProduct,
      effectiveRole,
      isSupportUser,
      canViewTeam,
      canModifyTeam,
      canManageOrgTeam,
      canViewOrgSettings,
      visibleProductsSettings,
      profileApiFetch,
      refreshUser,
      teamMembers,
      setTeamMembers,
      originalTeamMembers,
      setOriginalTeamMembers,
      loadingTeamMembers,
      setLoadingTeamMembers,
      reloadTeamMembers,
      rosterPersonsList,
      setRosterPersonsList,
      rosterFetchError,
      setRosterFetchError,
      teamSuggestionsLoading,
      setTeamSuggestionsLoading,
      teamSuggestionsError,
      setTeamSuggestionsError,
      teamSuggestionsData,
      setTeamSuggestionsData,
      teamSuggestionSelected,
      setTeamSuggestionSelected,
      teamSuggestionProductOverrides,
      setTeamSuggestionProductOverrides,
      expandedSuggestions,
      setExpandedSuggestions,
      handleFetchTeamSuggestions,
      handleBulkAddTeamSuggestions,
      savingCapacity,
      setSavingCapacity,
      teamRosterSaved,
      setTeamRosterSaved,
      teamRosterSaveError,
      setTeamRosterSaveError,
      newMember,
      setNewMember,
      addMemberRosterPick,
      setAddMemberRosterPick,
      addMemberSearchResults,
      setAddMemberSearchResults,
      addMemberSearchLoading,
      setAddMemberSearchLoading,
      handleAddTeamMember,
      handleDeleteTeamMember,
      handleUpdateTeamMember,
      persistPersonProductIds,
      availableRoles,
      setAvailableRoles,
      rolesList,
      setRolesList,
      showAddRoleInput,
      setShowAddRoleInput,
      showManageRoles,
      setShowManageRoles,
      newRoleName,
      setNewRoleName,
      editingRoleId,
      setEditingRoleId,
      editingRoleName,
      setEditingRoleName,
      newRoleInputRef,
      renameInputRef,
      loadRoles,
      handleToggleRoleInMapping,
      handleChangeCapacityCategory,
      handleRenameRole,
      handleDeleteRole,
      inviteEmail,
      setInviteEmail,
      inviteRole,
      setInviteRole,
      inviteLoading,
      setInviteLoading,
      inviteMessage,
      setInviteMessage,
      inviteError,
      setInviteError,
      inviteOrgProductIds,
      setInviteOrgProductIds,
      inviteRoleOptions,
      showInviteSection,
      handleInviteOrgMember,
      orgRbacMembers,
      setOrgRbacMembers,
      orgRbacLoading,
      setOrgRbacLoading,
      orgRbacError,
      setOrgRbacError,
      orgRbacRoleDraft,
      setOrgRbacRoleDraft,
      orgRbacSavingUserId,
      setOrgRbacSavingUserId,
      orgRbacOwnershipConfirm,
      setOrgRbacOwnershipConfirm,
      loadOrgRbacMembers,
      handleOrgRbacSaveRow,
      executeOrgRbacPatch,
      orgMemberRemoveTarget,
      setOrgMemberRemoveTarget,
      orgMemberRemoveLoading,
      setOrgMemberRemoveLoading,
      executeRemoveOrgMember,
      projectSettings,
      setProjectSettings,
      loadingProjectSettings,
      setLoadingProjectSettings,
      savingProjectSettings,
      setSavingProjectSettings,
      projectSettingsSaved,
      setProjectSettingsSaved,
      projectSettingsSaveError,
      setProjectSettingsSaveError,
      saveProjectSettings,
      rosterSearch,
      setRosterSearch,
      rosterSortBy,
      setRosterSortBy,
      rosterSortDir,
      setRosterSortDir,
      rosterPage,
      setRosterPage,
      rosterPageSize: ROSTER_PAGE_SIZE,
      rosterInviteModalPerson,
      setRosterInviteModalPerson,
      rosterInviteEmail,
      setRosterInviteEmail,
      rosterInviteRole,
      setRosterInviteRole,
      rosterInviteProductIds,
      setRosterInviteProductIds,
      rosterInviteLoading,
      setRosterInviteLoading,
      rosterInviteError,
      setRosterInviteError,
      openRosterInviteForPerson,
      handleSubmitRosterInvite,
      criteriaOptions,
      setCriteriaOptions,
      openCriteriaDropdown,
      setOpenCriteriaDropdown,
      criteriaSearchQueries,
      setCriteriaSearchQueries,
      criteriaDropdownRef,
      savingDistributionCriteria,
      setSavingDistributionCriteria,
      distributionCriteriaSaved,
      setDistributionCriteriaSaved,
      distributionCriteriaSaveError,
      setDistributionCriteriaSaveError,
      rosterProductToggleUserId,
      setRosterProductToggleUserId,
      estimationUnitsSectionRef,
      teamRosterSectionRef,
      teamAddMemberSectionRef,
      distributionCriteriaSectionRef,
      highlightEstimationUnitsSection,
      setHighlightEstimationUnitsSection,
      highlightTeamRosterSection,
      setHighlightTeamRosterSection,
      highlightTeamAddMemberSection,
      setHighlightTeamAddMemberSection,
      highlightDistributionCriteriaSection,
      setHighlightDistributionCriteriaSection,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentOrg,
      currentProduct,
      effectiveRole,
      isSupportUser,
      canViewTeam,
      canModifyTeam,
      canManageOrgTeam,
      canViewOrgSettings,
      visibleProductsSettings,
      profileApiFetch,
      refreshUser,
      teamMembers,
      originalTeamMembers,
      loadingTeamMembers,
      reloadTeamMembers,
      rosterPersonsList,
      rosterFetchError,
      teamSuggestionsLoading,
      teamSuggestionsError,
      teamSuggestionsData,
      teamSuggestionSelected,
      teamSuggestionProductOverrides,
      expandedSuggestions,
      handleFetchTeamSuggestions,
      handleBulkAddTeamSuggestions,
      savingCapacity,
      teamRosterSaved,
      teamRosterSaveError,
      newMember,
      addMemberRosterPick,
      addMemberSearchResults,
      addMemberSearchLoading,
      handleAddTeamMember,
      handleDeleteTeamMember,
      handleUpdateTeamMember,
      persistPersonProductIds,
      availableRoles,
      rolesList,
      showAddRoleInput,
      showManageRoles,
      newRoleName,
      editingRoleId,
      editingRoleName,
      loadRoles,
      handleToggleRoleInMapping,
      handleChangeCapacityCategory,
      handleRenameRole,
      handleDeleteRole,
      inviteEmail,
      inviteRole,
      inviteLoading,
      inviteMessage,
      inviteError,
      inviteOrgProductIds,
      inviteRoleOptions,
      showInviteSection,
      handleInviteOrgMember,
      orgRbacMembers,
      orgRbacLoading,
      orgRbacError,
      orgRbacRoleDraft,
      orgRbacSavingUserId,
      orgRbacOwnershipConfirm,
      loadOrgRbacMembers,
      handleOrgRbacSaveRow,
      executeOrgRbacPatch,
      orgMemberRemoveTarget,
      orgMemberRemoveLoading,
      executeRemoveOrgMember,
      projectSettings,
      loadingProjectSettings,
      savingProjectSettings,
      projectSettingsSaved,
      projectSettingsSaveError,
      saveProjectSettings,
      rosterSearch,
      rosterSortBy,
      rosterSortDir,
      rosterPage,
      rosterInviteModalPerson,
      rosterInviteEmail,
      rosterInviteRole,
      rosterInviteProductIds,
      rosterInviteLoading,
      rosterInviteError,
      openRosterInviteForPerson,
      handleSubmitRosterInvite,
      criteriaOptions,
      openCriteriaDropdown,
      criteriaSearchQueries,
      savingDistributionCriteria,
      distributionCriteriaSaved,
      distributionCriteriaSaveError,
      rosterProductToggleUserId,
      highlightEstimationUnitsSection,
      highlightTeamRosterSection,
      highlightTeamAddMemberSection,
      highlightDistributionCriteriaSection,
    ]
  );

  return (
    <div className="settings-layout">
      <SettingsSidebar
        activeSection={activeTab === 'project' ? 'integrations' : (activeTab as SettingsSection)}
        onSectionChange={handleSectionChange}
        teamMemberCount={teamMembers.length}
        trialActive={subscription?.status === 'trialing'}
        canViewOrgSettings={canViewOrgSettings}
        canViewBilling={canViewBilling}
        orgRoleLabel={currentOrg?.role ? orgRoleLabel(currentOrg.role) : undefined}
        collapsed={settingsSidebarCollapsed}
        onCollapsedChange={handleSettingsSidebarCollapsedChange}
      />

      <div className="settings-content">
        {/* Guided tour card — only for profile section */}
        {activeTab === 'profile' &&
          analyticsAtlasTour.hydrated &&
          !analyticsAtlasTour.missingContext &&
          !analyticsAtlasTour.isTerminal &&
          analyticsAtlasTour.step?.id === 'connect' && (
            <GuidedTourCard
              mission={analyticsMission}
              api={analyticsAtlasTour}
              renderStepBody={(id) => (id === 'connect' ? <AnalyticsConnectTourBody /> : null)}
              onBeforeAdvance={(stepId) => {
                if (stepId === 'connect') navigate('/analytics');
              }}
              renderIdle={(api) =>
                api.completed || api.skipped ? (
                  <button
                    type="button"
                    className="atlas-tour-restart-link forecast-tour-restart-link"
                    onClick={api.restart}
                  >
                    Restart guided tour
                  </button>
                ) : null
              }
            />
          )}

        {/* My Profile Section */}
        {activeTab === 'profile' && (
          <div className="profile-card">
            <div className="card-section">
              <h2 className="section-title">Personal Information</h2>
              <p className="section-description">
                Update your personal details that will be used across the application
              </p>

              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  <User className="label-icon" />
                  Full Name
                </label>
                <input
                  id="name"
                  type="text"
                  className="form-input"
                  placeholder="Enter your full name"
                  value={profile.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  <Mail className="label-icon" />
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  className="form-input"
                  placeholder="your.email@example.com"
                  readOnly={isSupportUser}
                  title={
                    isSupportUser
                      ? 'Your Relix sign-in email (from your account). Change it only in Supabase / account admin.'
                      : undefined
                  }
                  autoComplete={isSupportUser ? 'username' : 'email'}
                  value={personalInfoEmailDisplay}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                />
              </div>

              <div
                style={{
                  marginTop: '1.5rem',
                  paddingTop: '1.5rem',
                  borderTop: '1px solid var(--color-border, #e5e7eb)',
                }}
              >
                <h3
                  className="section-title"
                  style={{ fontSize: '1.05rem', marginBottom: '0.35rem' }}
                >
                  Password
                </h3>
                <p className="section-description" style={{ marginBottom: '1rem' }}>
                  Update your sign-in password with your current password, or request an email link
                  if you do not remember it (opens a secure page to set a new password).
                </p>

                <div className="form-group">
                  <label htmlFor="profile-password-current" className="form-label">
                    <Lock className="label-icon" aria-hidden />
                    Current password
                  </label>
                  <div
                    style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}
                  >
                    <input
                      id="profile-password-current"
                      type={showPasswordCurrent ? 'text' : 'password'}
                      className="form-input"
                      style={{ flex: '1 1 220px', minWidth: 0 }}
                      autoComplete="current-password"
                      value={passwordCurrent}
                      onChange={(e) => setPasswordCurrent(e.target.value)}
                      placeholder="Required to change password"
                    />
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => setShowPasswordCurrent((v) => !v)}
                      aria-pressed={showPasswordCurrent}
                    >
                      {showPasswordCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="profile-password-new" className="form-label">
                    <Lock className="label-icon" aria-hidden />
                    New password
                  </label>
                  <div
                    style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}
                  >
                    <input
                      id="profile-password-new"
                      type={showPasswordNew ? 'text' : 'password'}
                      className="form-input"
                      style={{ flex: '1 1 220px', minWidth: 0 }}
                      autoComplete="new-password"
                      value={passwordNew}
                      onChange={(e) => setPasswordNew(e.target.value)}
                      placeholder="At least 6 characters"
                      minLength={6}
                    />
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => setShowPasswordNew((v) => !v)}
                      aria-pressed={showPasswordNew}
                    >
                      {showPasswordNew ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="profile-password-confirm" className="form-label">
                    <Lock className="label-icon" aria-hidden />
                    Confirm new password
                  </label>
                  <input
                    id="profile-password-confirm"
                    type={showPasswordNew ? 'text' : 'password'}
                    className="form-input"
                    autoComplete="new-password"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    placeholder="Repeat new password"
                    minLength={6}
                  />
                </div>

                <div
                  style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}
                >
                  <button
                    type="button"
                    className="save-button"
                    disabled={passwordChanging || resetEmailSending}
                    onClick={() => void handleUpdatePassword()}
                  >
                    {passwordChanging ? (
                      <>
                        <Loader2 className="button-icon profile-settings-spin" size={16} />
                        Updating…
                      </>
                    ) : (
                      'Update password'
                    )}
                  </button>
                </div>

                <p
                  className="section-description"
                  style={{ marginTop: '1.25rem', marginBottom: '0.5rem' }}
                >
                  <strong>Forgot your current password?</strong> We can send a reset link to your
                  account email{user?.email ? ` (${user.email})` : ''}.
                </p>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={passwordChanging || resetEmailSending}
                  onClick={() => void handleSendPasswordResetEmail()}
                >
                  {resetEmailSending ? (
                    <>
                      <Loader2 className="button-icon profile-settings-spin" size={16} />
                      Sending…
                    </>
                  ) : (
                    'Email me a reset link'
                  )}
                </button>

                {passwordSectionError ? (
                  <div className="error-message" style={{ marginTop: '12px' }} role="alert">
                    <span className="error-icon">!</span>
                    {passwordSectionError}
                  </div>
                ) : null}
                {passwordSectionMessage ? (
                  <div className="success-message" style={{ marginTop: '12px' }} role="status">
                    <span className="success-icon">✓</span>
                    {passwordSectionMessage}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="card-actions" style={{ flexWrap: 'wrap' }}>
              <button
                type="button"
                className="save-button"
                disabled={isSaving}
                onClick={() => {
                  pushSaveDiag('UI: click Save (My Profile tab)');
                  void handleSave();
                }}
              >
                {isSaving ? (
                  <Loader2 className="button-icon profile-settings-spin" />
                ) : (
                  <Save className="button-icon" />
                )}
                {isSaving ? 'Saving…' : 'Save Changes'}
              </button>
              {isSaved && (
                <div className="success-message">
                  <span className="success-icon">✓</span>
                  Profile saved successfully!
                </div>
              )}
              {saveError && activeTab === 'profile' && (
                <div className="error-message" style={{ width: '100%' }} role="alert">
                  <span className="error-icon">!</span>
                  {saveError}
                </div>
              )}
            </div>
            <AuditLogSection />
            <div className="card-section" style={{ marginTop: '24px' }}>
              <h2 className="section-title">Product tips &amp; tour</h2>
              <p className="section-description">
                Show the welcome tour and page tips again (Analytics, Calendar, Releases, Reports,
                Forecast, Commitment) and reset feature hints (e.g. Add row, product switcher).
                Useful after updates or when demoing the app. If you chose <strong>Close</strong> in
                the Atlas panel to hide the floating helper, use <strong>Restore Atlas</strong> to
                show it again. Closing Atlas is not the same as skipping steps inside a guided tour;
                on-page tours still have their own Skip controls.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                  onClick={() => {
                    resetAllOnboarding();
                    setOnboardingResetMessage(
                      'Onboarding reset. Open another page or refresh to see the welcome tour and tips again.'
                    );
                  }}
                >
                  <RotateCcw size={16} aria-hidden />
                  Reset onboarding
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                  disabled={!atlas || !currentOrg?.id}
                  onClick={() => {
                    if (!atlas) return;
                    atlas.restoreNavigator();
                    setAtlasRestoreMessage(
                      'The Atlas helper is available again. Open the Relix Atlas control in the corner when you are ready.'
                    );
                  }}
                >
                  <Sparkles size={16} aria-hidden />
                  Restore Atlas
                </button>
              </div>
              {onboardingResetMessage ? (
                <p
                  style={{
                    marginTop: '12px',
                    fontSize: '14px',
                    color: 'var(--color-success, #22c55e)',
                  }}
                  role="status"
                >
                  {onboardingResetMessage}
                </p>
              ) : null}
              {atlasRestoreMessage ? (
                <p
                  style={{
                    marginTop: '12px',
                    fontSize: '14px',
                    color: 'var(--color-success, #22c55e)',
                  }}
                  role="status"
                >
                  {atlasRestoreMessage}
                </p>
              ) : null}
            </div>
          </div>
        )}

        {/* Project Settings / Integrations Section */}
        {(activeTab === 'project' || activeTab === 'integrations') && (
          <div className="profile-card">
            {canConfigureIntegrations ? (
              <>
                <div className="card-section">
                  <h2 className="section-title">Organization</h2>
                  <p className="section-description">
                    Set how your organization appears in the app. The Organization ID is a stable
                    technical identifier (not editable).
                  </p>
                  <div className="form-group">
                    <label htmlFor="orgDisplayName" className="form-label">
                      <Settings className="label-icon" />
                      Organization display name
                    </label>
                    <input
                      id="orgDisplayName"
                      type="text"
                      className="form-input"
                      placeholder="e.g. Acme Corp"
                      value={orgDisplayName}
                      disabled={!canUpdateOrg}
                      maxLength={100}
                      onChange={(e) => {
                        setOrgDisplayName(e.target.value);
                        setOrgNameSaveError(null);
                      }}
                    />
                    <span className="form-hint">
                      {canUpdateOrg
                        ? 'Shown in settings and navigation. Trimmed; 1–100 characters.'
                        : 'Only the organization owner can change the organization name.'}
                    </span>
                  </div>
                  <div className="form-group">
                    <label htmlFor="orgIdReadonly" className="form-label">
                      Organization ID
                    </label>
                    <div
                      style={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      <input
                        id="orgIdReadonly"
                        type="text"
                        readOnly
                        className="form-input"
                        value={currentOrg?.id || ''}
                        style={{ fontFamily: 'ui-monospace, monospace', fontSize: '13px' }}
                      />
                      <button
                        type="button"
                        className="save-button"
                        style={{ padding: '8px 14px', fontSize: '13px' }}
                        onClick={() => {
                          if (currentOrg?.id && navigator.clipboard?.writeText) {
                            void navigator.clipboard.writeText(currentOrg.id);
                          }
                        }}
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                  {canUpdateOrg && (
                    <div className="card-actions" style={{ marginTop: 8 }}>
                      <button
                        type="button"
                        className="save-button"
                        disabled={orgNameSaving}
                        onClick={() => void handleSaveOrgDisplayName()}
                      >
                        {orgNameSaving ? (
                          <Loader2 className="button-icon profile-settings-spin" />
                        ) : (
                          <Save className="button-icon" />
                        )}
                        {orgNameSaving ? 'Saving…' : 'Save organization name'}
                      </button>
                    </div>
                  )}
                  {orgNameSaveError && (
                    <div className="error-message" style={{ marginTop: 8 }} role="alert">
                      <span className="error-icon">!</span>
                      {orgNameSaveError}
                    </div>
                  )}
                  {orgNameSaved && (
                    <div className="success-message" role="status" style={{ marginTop: 8 }}>
                      <span className="success-icon">✓</span>
                      Organization name saved successfully!
                    </div>
                  )}
                </div>

                <div className="card-section" id="profile-project-jira-anchor">
                  <span id="settings-org-jira-anchor" aria-hidden="true" />
                  <h2 className="section-title">Organization Jira account</h2>
                  <p className="section-description">
                    One Jira API token and email apply to the whole organization. Each product uses
                    its own Jira project key (set in Per-product integrations below).
                  </p>

                  <div className="form-group">
                    <label htmlFor="jiraProject" className="form-label">
                      <FolderGit2 className="label-icon" />
                      Default Jira project key (org fallback)
                    </label>
                    <input
                      id="jiraProject"
                      type="text"
                      className="form-input"
                      placeholder="e.g. PROJ, ABC"
                      value={profile.jiraProject}
                      onChange={(e) =>
                        handleInputChange('jiraProject', e.target.value.toUpperCase())
                      }
                    />
                    <span className="form-hint">
                      Org fallback project key. Per-product keys are edited below. Use{' '}
                      <strong>Save Jira connection</strong> in this section or{' '}
                      <strong>Save all</strong> at the bottom.
                    </span>
                  </div>

                  <div className="form-group">
                    <label htmlFor="jiraToken" className="form-label">
                      <Key className="label-icon" />
                      Jira API Token
                    </label>
                    <div className="input-with-icon">
                      <input
                        id="jiraToken"
                        type={showToken ? 'text' : 'password'}
                        className="form-input"
                        placeholder="Enter your Jira API token"
                        value={profile.jiraToken}
                        onChange={(e) => handleInputChange('jiraToken', e.target.value)}
                      />
                      <button
                        type="button"
                        className="toggle-visibility-btn"
                        onClick={() => setShowToken(!showToken)}
                        title={showToken ? 'Hide token' : 'Show token'}
                      >
                        {showToken ? <EyeOff className="icon" /> : <Eye className="icon" />}
                      </button>
                    </div>
                    <span className="form-hint">
                      Generate your token at:{' '}
                      <a
                        href="https://id.atlassian.com/manage-profile/security/api-tokens"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="form-link"
                      >
                        Atlassian API Tokens
                      </a>
                    </span>
                  </div>
                  <div className="card-actions" id="settings-test-anchor" style={{ marginTop: 12 }}>
                    <button
                      type="button"
                      className="save-button"
                      disabled={
                        jiraSectionSaving ||
                        isSaving ||
                        sharePointSectionSaving ||
                        productIntegrationSaving
                      }
                      onClick={() => void handleSaveJiraSection()}
                    >
                      {jiraSectionSaving ? (
                        <Loader2 className="button-icon profile-settings-spin" />
                      ) : (
                        <Save className="button-icon" />
                      )}
                      {jiraSectionSaving ? 'Saving…' : 'Save Jira connection'}
                    </button>
                  </div>
                  {jiraSectionError && (
                    <div className="error-message" style={{ marginTop: 12 }} role="alert">
                      <span className="error-icon">!</span>
                      {jiraSectionError}
                    </div>
                  )}
                  {jiraSectionSaved && (
                    <div className="success-message" role="status" style={{ marginTop: 12 }}>
                      <span className="success-icon">✓</span>
                      Jira connection saved successfully!
                    </div>
                  )}
                </div>

                <div
                  ref={sharePointOrgSectionRef}
                  id="settings-sharepoint-org-section"
                  className={`card-section${highlightSharePointOrgSection ? ' card-section--settings-highlight' : ''}`}
                >
                  <h2 className="section-title">Organization SharePoint connection</h2>
                  <p className="section-description">
                    Azure AD app credentials and site URL are shared by the organization. Link each
                    product to its own calendar list in Per-product integrations. Use{' '}
                    <strong>Save SharePoint connection</strong> in this section or{' '}
                    <strong>Save all</strong> at the bottom. Per-product list and calendar fields
                    use <strong>Save this product</strong> below.
                  </p>

                  <div className="form-group">
                    <label htmlFor="sharePointSiteUrl" className="form-label">
                      <Calendar className="label-icon" />
                      SharePoint site URL
                    </label>
                    <input
                      id="sharePointSiteUrl"
                      type="text"
                      className="form-input"
                      placeholder="https://yourtenant.sharepoint.com/sites/YourSite"
                      value={profile.sharePointSiteUrl}
                      onChange={(e) => handleInputChange('sharePointSiteUrl', e.target.value)}
                    />
                    <span className="form-hint">
                      The URL of your SharePoint site containing the calendar
                    </span>
                  </div>

                  <div
                    style={{
                      marginTop: '24px',
                      paddingTop: '24px',
                      borderTop: '1px solid var(--color-border)',
                    }}
                  >
                    <h3
                      style={{
                        marginBottom: '16px',
                        fontSize: '16px',
                        fontWeight: '600',
                        color: 'var(--color-text)',
                      }}
                    >
                      Azure AD / Microsoft Graph API Configuration
                    </h3>
                    <p
                      style={{
                        marginBottom: '16px',
                        fontSize: '14px',
                        color: 'var(--color-text-secondary)',
                      }}
                    >
                      Required for Microsoft Graph API integration. Get these values from your Azure
                      AD app registration at{' '}
                      <a
                        href="https://entra.microsoft.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#3b82f6', textDecoration: 'underline' }}
                      >
                        Azure Portal
                      </a>
                    </p>

                    <div className="form-group">
                      <label htmlFor="sharePointClientId" className="form-label">
                        <Key className="label-icon" />
                        Application (Client) ID <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input
                        id="sharePointClientId"
                        type="text"
                        className="form-input"
                        placeholder="Enter your Application (Client) ID"
                        value={profile.sharePointClientId || ''}
                        onChange={(e) => handleInputChange('sharePointClientId', e.target.value)}
                      />
                      <span className="form-hint">
                        The Client ID of your Azure AD application registration (from Essentials
                        section)
                      </span>
                    </div>

                    <div className="form-group">
                      <label htmlFor="sharePointTenantId" className="form-label">
                        <Key className="label-icon" />
                        Directory (Tenant) ID <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <input
                        id="sharePointTenantId"
                        type="text"
                        className="form-input"
                        placeholder="Enter your Directory (Tenant) ID"
                        value={profile.sharePointTenantId || ''}
                        onChange={(e) => handleInputChange('sharePointTenantId', e.target.value)}
                      />
                      <span className="form-hint">
                        The Tenant ID of your Azure AD directory (from Essentials section)
                      </span>
                    </div>

                    <div className="form-group">
                      <label htmlFor="sharePointObjectId" className="form-label">
                        <Key className="label-icon" />
                        Object ID (Optional)
                      </label>
                      <input
                        id="sharePointObjectId"
                        type="text"
                        className="form-input"
                        placeholder="Enter your Object ID (optional)"
                        value={profile.sharePointObjectId || ''}
                        onChange={(e) => handleInputChange('sharePointObjectId', e.target.value)}
                      />
                      <span className="form-hint">
                        Optional: The Object ID of your Azure AD application (from Essentials
                        section, for reference)
                      </span>
                    </div>

                    <div className="form-group">
                      <label htmlFor="sharePointClientSecret" className="form-label">
                        <Key className="label-icon" />
                        Client Secret <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                      <div className="input-with-icon">
                        <input
                          id="sharePointClientSecret"
                          type={showSharePointToken ? 'text' : 'password'}
                          className="form-input"
                          placeholder="Enter your Azure AD Client Secret"
                          value={profile.sharePointClientSecret || ''}
                          onChange={(e) =>
                            handleInputChange('sharePointClientSecret', e.target.value)
                          }
                        />
                        <button
                          type="button"
                          className="toggle-visibility-btn"
                          onClick={() => setShowSharePointToken(!showSharePointToken)}
                          title={showSharePointToken ? 'Hide Client Secret' : 'Show Client Secret'}
                        >
                          {showSharePointToken ? (
                            <EyeOff className="icon" />
                          ) : (
                            <Eye className="icon" />
                          )}
                        </button>
                      </div>
                      <span className="form-hint">
                        <strong>How to get Client Secret:</strong>
                        <br />
                        1. Go to{' '}
                        <a
                          href="https://entra.microsoft.com/#view/Microsoft_AAD_RegisteredApps/ApplicationMenuBlade/~/CertificatesAndSecrets/appId/08c8fe13-f620-4bdd-a090-4f7c20b59f01/isMSAApp~/false"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#3b82f6', textDecoration: 'underline' }}
                        >
                          Certificates & secrets
                        </a>{' '}
                        in Azure Portal
                        <br />
                        2. In the "Client secrets" section, click on "1 secret" (or create a new one
                        if none exists)
                        <br />
                        3. Copy the "Value" of the secret (it's shown only once when created)
                        <br />
                        4. Paste it here
                        <br />
                        <strong>Important:</strong> Make sure the app has been granted permissions
                        to the SharePoint site by an administrator.
                      </span>
                    </div>
                  </div>
                  <div className="card-actions" style={{ marginTop: 16 }}>
                    <button
                      type="button"
                      className="save-button"
                      disabled={
                        sharePointSectionSaving ||
                        isSaving ||
                        jiraSectionSaving ||
                        productIntegrationSaving
                      }
                      onClick={() => void handleSaveSharePointSection()}
                    >
                      {sharePointSectionSaving ? (
                        <Loader2 className="button-icon profile-settings-spin" />
                      ) : (
                        <Save className="button-icon" />
                      )}
                      {sharePointSectionSaving ? 'Saving…' : 'Save SharePoint connection'}
                    </button>
                  </div>
                  {sharePointSectionError && (
                    <div className="error-message" style={{ marginTop: 12 }} role="alert">
                      <span className="error-icon">!</span>
                      {sharePointSectionError}
                    </div>
                  )}
                  {sharePointSectionSaved && (
                    <div className="success-message" role="status" style={{ marginTop: 12 }}>
                      <span className="success-icon">✓</span>
                      SharePoint connection saved successfully!
                    </div>
                  )}
                </div>

                <div
                  className="card-section profile-product-integration-section"
                  id="product-integrations-section"
                >
                  <span id="settings-product-jira-anchor" aria-hidden="true" />
                  <h2 className="section-title">Per-product integrations</h2>
                  <p className="section-description">
                    Jira project key and SharePoint calendar mapping are stored per product. Switch
                    products here or from the header. Use <strong>Save this product</strong> for
                    only this product, or <strong>Save all</strong> at the bottom to save everything
                    on this tab. <strong>Calendar</strong> loads and SharePoint sync use the product
                    selected in the <strong>header</strong> (not this dropdown alone). Switch the
                    header to RDM before opening Calendar if you want the RDM list.
                  </p>

                  {!visibleProductsSettings.length ? (
                    <p className="profile-integration-empty">
                      No products in this organization yet.
                      {canCreateProduct ? (
                        <>
                          {' '}
                          <button
                            type="button"
                            className="profile-link-button"
                            onClick={() => setAddProductModalOpen(true)}
                          >
                            Add a product
                          </button>
                        </>
                      ) : null}
                    </p>
                  ) : (
                    <>
                      <div className="form-group">
                        <label htmlFor="settings-product-select" className="form-label">
                          <Settings className="label-icon" />
                          Product
                        </label>
                        <CustomSelect
                          id="settings-product-select"
                          value={settingsProductId ?? ''}
                          options={visibleProductsSettings.map((p) => ({
                            value: p.id,
                            label: `${productCompactDisplayLabel(p)}${p.is_default ? ' — Default' : ''}`,
                          }))}
                          onChange={(val) => setSettingsProductId(val || null)}
                        />
                      </div>

                      {selectedSettingsProduct ? (
                        <>
                          <div className="form-group" style={{ marginBottom: 20 }}>
                            <label htmlFor="productDisplayName" className="form-label">
                              Product name
                            </label>
                            <input
                              id="productDisplayName"
                              type="text"
                              className="form-input"
                              value={productDraft.name}
                              onChange={(e) =>
                                setProductDraft((d) => ({ ...d, name: e.target.value }))
                              }
                              placeholder="e.g. Product name"
                              maxLength={120}
                              autoComplete="off"
                              disabled={!canUpdateProduct}
                              aria-describedby="productDisplayName-hint"
                            />
                            <span id="productDisplayName-hint" className="form-hint">
                              Shown in the header product switcher. The Jira project key appears on
                              the second line below this name.
                              {!canUpdateProduct
                                ? ' Only the organization owner can change the product name.'
                                : ''}
                            </span>
                          </div>
                          <div className="profile-product-integration-grid">
                            <div className="profile-product-integration-col">
                              <h3 className="profile-product-integration-subtitle">Jira</h3>
                              <p className="section-description" style={{ marginBottom: 12 }}>
                                Project key for this product. Jira domain:{' '}
                                <strong>
                                  {profile.jiraDomain || 'hrsinternational.atlassian.net'}
                                </strong>{' '}
                                (org-level).
                              </p>
                              <div className="form-group">
                                <label htmlFor="productJiraKey" className="form-label">
                                  Jira project key
                                </label>
                                <input
                                  id="productJiraKey"
                                  type="text"
                                  className="form-input"
                                  value={productDraft.jira_project_key}
                                  onChange={(e) =>
                                    setProductDraft((d) => ({
                                      ...d,
                                      jira_project_key: e.target.value.toUpperCase(),
                                    }))
                                  }
                                  placeholder="e.g. PROJ"
                                  disabled={!canUpdateProduct}
                                />
                              </div>
                            </div>
                            <div className="profile-product-integration-col">
                              <h3 className="profile-product-integration-subtitle">
                                SharePoint calendar
                              </h3>
                              <p className="section-description" style={{ marginBottom: 12 }}>
                                Same site as organization (
                                {profile.sharePointSiteUrl ? (
                                  <span style={{ wordBreak: 'break-all' }}>
                                    {profile.sharePointSiteUrl}
                                  </span>
                                ) : (
                                  'not set'
                                )}
                                ). Set the list or calendar URL for this product.
                              </p>
                              <div className="form-group">
                                <label htmlFor="productCalName" className="form-label">
                                  Calendar label
                                </label>
                                <input
                                  id="productCalName"
                                  type="text"
                                  className="form-input"
                                  value={productDraft.sharepoint_calendar_name}
                                  onChange={(e) =>
                                    setProductDraft((d) => ({
                                      ...d,
                                      sharepoint_calendar_name: e.target.value,
                                    }))
                                  }
                                  placeholder="e.g. Team calendar"
                                  disabled={!canUpdateProduct}
                                />
                              </div>
                              <div className="form-group">
                                <label htmlFor="productCalUrl" className="form-label">
                                  Calendar web URL (optional)
                                </label>
                                <input
                                  id="productCalUrl"
                                  type="text"
                                  className="form-input"
                                  value={productDraft.sharepoint_calendar_url}
                                  onChange={(e) =>
                                    setProductDraft((d) => ({
                                      ...d,
                                      sharepoint_calendar_url: e.target.value,
                                    }))
                                  }
                                  placeholder="https://…"
                                  disabled={!canUpdateProduct}
                                />
                              </div>
                              <div className="form-group">
                                <label htmlFor="productListId" className="form-label">
                                  SharePoint list ID
                                </label>
                                <input
                                  id="productListId"
                                  type="text"
                                  className="form-input"
                                  value={productDraft.sharepoint_list_id}
                                  onChange={(e) =>
                                    setProductDraft((d) => ({
                                      ...d,
                                      sharepoint_list_id: e.target.value,
                                    }))
                                  }
                                  placeholder="{GUID}"
                                  disabled={!canUpdateProduct}
                                />
                                <span className="form-hint">
                                  To find the list GUID: in SharePoint open Site contents → your
                                  calendar list → List settings (or ⋯ → Settings). The browser URL
                                  includes{' '}
                                  <code style={{ fontSize: '0.9em' }}>listedit.aspx?List=</code>{' '}
                                  followed by the GUID (hyphens may show as{' '}
                                  <code style={{ fontSize: '0.9em' }}>%2D</code>
                                  ). Copy that value here (braces optional, e.g. {'{GUID}'}).
                                  Example (not a real site):{' '}
                                  <code style={{ fontSize: '0.85em', wordBreak: 'break-all' }}>
                                    https://contoso.sharepoint.com/sites/ExampleProject/_layouts/15/listedit.aspx?List=aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee
                                  </code>
                                  . Use a different list per product on the same site if needed.
                                </span>
                              </div>
                              <button
                                type="button"
                                className="save-button"
                                style={{
                                  marginTop: 8,
                                  backgroundColor: 'var(--color-success, #10b981)',
                                }}
                                onClick={() => void handleTestProductCalendar()}
                                title={
                                  !profile.sharePointSiteUrl?.trim()
                                    ? 'Set and save organization SharePoint site URL above first'
                                    : undefined
                                }
                                disabled={
                                  testingProductCalendar ||
                                  productIntegrationSaving ||
                                  !profile.sharePointSiteUrl ||
                                  !canUpdateProduct
                                }
                              >
                                {testingProductCalendar ? (
                                  <>Testing…</>
                                ) : (
                                  <>
                                    <CheckCircle className="button-icon" />
                                    Test Azure / SharePoint connection
                                  </>
                                )}
                              </button>
                              {productCalendarTestResult ? (
                                <p
                                  style={{
                                    marginTop: 10,
                                    fontSize: 14,
                                    color: productCalendarTestResult.startsWith('Azure')
                                      ? 'var(--color-success, #059669)'
                                      : 'var(--color-error, #dc2626)',
                                  }}
                                >
                                  {productCalendarTestResult}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        </>
                      ) : null}

                      {productIntegrationError ? (
                        <div className="error-message" style={{ marginTop: 8 }} role="alert">
                          <span className="error-icon">!</span>
                          {productIntegrationError}
                        </div>
                      ) : null}
                      {productIntegrationSaved ? (
                        <div className="success-message" role="status" style={{ marginTop: 12 }}>
                          <span className="success-icon">✓</span>
                          Product saved successfully!
                        </div>
                      ) : null}

                      <div className="card-actions" style={{ marginTop: 16 }}>
                        <button
                          type="button"
                          className="save-button"
                          disabled={
                            productIntegrationSaving ||
                            !selectedSettingsProduct ||
                            !canUpdateProduct ||
                            isSaving ||
                            jiraSectionSaving ||
                            sharePointSectionSaving
                          }
                          onClick={() => void handleSaveProductIntegration()}
                        >
                          {productIntegrationSaving ? (
                            <Loader2 className="button-icon profile-settings-spin" />
                          ) : (
                            <Save className="button-icon" />
                          )}
                          {productIntegrationSaving ? 'Saving…' : 'Save this product'}
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {visibleProductsSettings.length > 0 ? (
                  <div className="card-section profile-integration-map-section">
                    <h2 className="section-title">Integration map</h2>
                    <p className="section-description">
                      Overview of all products. Select a card to edit that product above.
                    </p>
                    <div className="profile-integration-map-grid">
                      {visibleProductsSettings.map((p) => (
                        <div
                          key={p.id}
                          className={`profile-integration-map-card ${
                            settingsProductId === p.id ? 'profile-integration-map-card--active' : ''
                          }`}
                        >
                          <button
                            type="button"
                            className="profile-integration-map-card-main"
                            onClick={() => {
                              setSettingsProductId(p.id);
                              document
                                .getElementById('product-integrations-section')
                                ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }}
                          >
                            <div className="profile-integration-map-card-title">
                              <span
                                className={`profile-integration-dot ${
                                  productIntegrationConfigured(p)
                                    ? 'profile-integration-dot--ok'
                                    : 'profile-integration-dot--muted'
                                }`}
                                aria-hidden
                              />
                              <span>{productCompactDisplayLabel(p)}</span>
                              {p.is_default ? (
                                <span className="product-switcher-default-badge">Default</span>
                              ) : null}
                            </div>
                            <div className="profile-integration-map-row">
                              <span className="profile-integration-map-label">Jira</span>
                              <span>
                                {p.jira_project_key && String(p.jira_project_key).trim()
                                  ? p.jira_project_key
                                  : 'Not set'}
                              </span>
                            </div>
                            <div className="profile-integration-map-row">
                              <span className="profile-integration-map-label">Calendar</span>
                              <span>
                                {p.sharepoint_calendar_name?.trim() ||
                                p.sharepoint_list_id?.trim() ||
                                p.sharepoint_calendar_url?.trim()
                                  ? p.sharepoint_calendar_name ||
                                    p.sharepoint_list_id ||
                                    p.sharepoint_calendar_url ||
                                    'Configured'
                                  : 'Not configured'}
                              </span>
                            </div>
                          </button>
                          <div className="profile-integration-map-card-actions">
                            <button
                              type="button"
                              className="profile-icon-btn"
                              title="Edit in section above"
                              aria-label={`Edit ${p.name}`}
                              onClick={() => {
                                setSettingsProductId(p.id);
                                document
                                  .getElementById('product-integrations-section')
                                  ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                              }}
                            >
                              <Pencil size={16} />
                            </button>
                            {canDeleteProduct ? (
                              <button
                                type="button"
                                className="profile-icon-btn profile-icon-btn--danger"
                                title="Delete product"
                                aria-label={`Delete ${p.name}`}
                                onClick={() => setDeleteConfirmProduct(p)}
                              >
                                <Trash2 size={16} />
                              </button>
                            ) : null}
                          </div>
                        </div>
                      ))}
                      {canCreateProduct ? (
                        <button
                          type="button"
                          className="profile-integration-map-card profile-integration-map-add"
                          onClick={() => setAddProductModalOpen(true)}
                        >
                          <Plus size={24} aria-hidden />
                          <span>Add product</span>
                        </button>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {deleteConfirmProduct ? (
                  <div
                    className="add-product-modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="delete-product-title"
                    onClick={(e) => {
                      if (e.target === e.currentTarget && !productDeleteLoading)
                        setDeleteConfirmProduct(null);
                    }}
                  >
                    <div
                      className="add-product-modal-content"
                      onClick={(ev) => ev.stopPropagation()}
                    >
                      <div className="add-product-modal-header">
                        <h2 id="delete-product-title">Delete product?</h2>
                      </div>
                      <div className="add-product-modal-body">
                        <p style={{ margin: 0 }}>
                          Delete <strong>{deleteConfirmProduct.name}</strong>? This cannot be
                          undone. Calendar events for this product may be affected.
                        </p>
                      </div>
                      <div className="add-product-modal-footer">
                        <button
                          type="button"
                          className="add-product-modal-btn-secondary"
                          disabled={productDeleteLoading}
                          onClick={() => setDeleteConfirmProduct(null)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="add-product-modal-btn-primary"
                          style={{ background: 'var(--color-error, #dc2626)' }}
                          disabled={productDeleteLoading}
                          onClick={() => void handleConfirmDeleteProduct()}
                        >
                          {productDeleteLoading ? 'Deleting…' : 'Delete'}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}

                {currentOrg ? (
                  <AddProductModal
                    isOpen={addProductModalOpen}
                    onClose={() => setAddProductModalOpen(false)}
                    organizationId={currentOrg.id}
                    userEmail={user?.email ?? undefined}
                    onUseExistingProduct={async ({ id }) => {
                      const p = currentOrg.products?.find((x) => x.id === id);
                      if (p) {
                        setSettingsProductId(id);
                        selectProduct(p);
                      }
                      await refreshUser();
                    }}
                    onCreated={async (product) => {
                      await refreshUser();
                      setSettingsProductId(product.id);
                      selectProduct(product);
                      setAddProductModalOpen(false);
                    }}
                  />
                ) : null}
              </>
            ) : null}

            {/* Commitment source fields (scope + delivery) */}
            <CommitmentSourceConfigSection />

            {/* Jira role fields (Developer + QA) — drive Forecast Sprint view
                grouping + role-aware drag-and-drop write-back. Deep-linkable
                via ?tab=project&section=jira-role-fields from the Sprint/Hybrid
                empty-state hint. */}
            <div
              ref={jiraRoleFieldsSectionRef}
              id="profile-jira-role-fields"
              className={
                highlightJiraRoleFieldsSection
                  ? 'profile-forecast-custom-fields-highlight'
                  : undefined
              }
            >
              <JiraRoleFieldsSection />
            </div>

            {/* Forecast custom fields (filters / table column / summary breakdown) */}
            <div
              ref={forecastCustomFieldsSectionRef}
              id="profile-forecast-custom-fields"
              className={
                highlightForecastCustomFieldsSection
                  ? 'profile-forecast-custom-fields-highlight'
                  : undefined
              }
            >
              <ForecastCustomFieldsSection />
            </div>

            {/* Reports — Branding & system requirements (Iteration 4).
                Subsumes the Iteration-3 Release Notes field picker. The
                #release-notes-field anchor is preserved on its subsection so
                deep-links from the Reports PreflightBar still land precisely. */}
            <ProductBrandingPanel productId={currentProduct?.id ?? null} />

            {/* Delivery Review settings group — Commitment, Estimation, Capacity pillars */}
            <div
              id="delivery-review-settings"
              className="delivery-review-settings-panel"
              style={{ marginTop: '1.5rem' }}
            >
              <header className="delivery-review-settings-panel__header">
                <h2>Delivery review settings</h2>
                <p>
                  Configure the three scoring pillars used by Delivery Review: capacity utilization,
                  commitment adherence, and estimation accuracy. Each subsection keeps its own
                  per-product override.
                </p>
              </header>

              {/* Delivery Pillars Thresholds — weight distribution */}
              <div id="profile-pillar-weights">
                <PillarWeightsPanel />
              </div>

              {/* Commitment scoring profile */}
              <div id="profile-commitment-scoring">
                <CommitmentScoringPanel />
              </div>

              {/* Estimation settings */}
              <div
                ref={estimationTargetSectionRef}
                id="profile-estimation-target"
                className={highlightEstimationTargetSection ? 'forecast-tour-highlight' : undefined}
                style={highlightEstimationTargetSection ? { borderRadius: '16px' } : undefined}
              >
                <EstimationTargetPanel />
              </div>

              {/* Capacity scoring thresholds */}
              <div
                id="profile-capacity-scoring"
                className={highlightCapacityScoringSection ? 'forecast-tour-highlight' : undefined}
                style={highlightCapacityScoringSection ? { borderRadius: '16px' } : undefined}
              >
                <CapacityScoringPanel />
              </div>
            </div>

            {/* Quality settings (Analytics → Quality tab): failure / QA / dev statuses,
                bug types, and the Bug Type classifier custom field. */}
            <div
              ref={profileQualitySettingsSectionRef}
              id="profile-quality-settings"
              style={{ marginTop: '1.5rem' }}
            >
              <QualitySettingsPanel />
            </div>

            {canConfigureIntegrations ? (
              <div className="card-actions" style={{ flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="save-button"
                  disabled={
                    isSaving ||
                    jiraSectionSaving ||
                    sharePointSectionSaving ||
                    productIntegrationSaving
                  }
                  onPointerDown={() => pushSaveDiag('UI: pointerdown on Save all (Project tab)')}
                  onClick={() => {
                    pushSaveDiag('UI: click Save all (Project tab)');
                    void handleSave();
                  }}
                >
                  {isSaving ? (
                    <Loader2 className="button-icon profile-settings-spin" />
                  ) : (
                    <Save className="button-icon" />
                  )}
                  {isSaving ? 'Saving…' : 'Save all'}
                </button>

                <button
                  type="button"
                  className="save-button"
                  onClick={handleTestSharePoint}
                  disabled={
                    testingSharePoint ||
                    !profile.sharePointSiteUrl ||
                    isSaving ||
                    jiraSectionSaving ||
                    sharePointSectionSaving ||
                    productIntegrationSaving
                  }
                  style={{
                    marginLeft: '12px',
                    backgroundColor: '#10b981',
                    opacity: testingSharePoint || !profile.sharePointSiteUrl ? 0.6 : 1,
                    cursor:
                      testingSharePoint || !profile.sharePointSiteUrl ? 'not-allowed' : 'pointer',
                  }}
                >
                  {testingSharePoint ? (
                    <>⏳ Testing...</>
                  ) : (
                    <>
                      <CheckCircle className="button-icon" />
                      Test SharePoint Connection
                    </>
                  )}
                </button>

                {sharePointTestResult && (
                  <div
                    style={{
                      marginTop: '12px',
                      padding: '12px',
                      borderRadius: '6px',
                      backgroundColor: sharePointTestResult.includes('successful')
                        ? '#d1fae5'
                        : '#fee2e2',
                      color: sharePointTestResult.includes('successful') ? '#065f46' : '#991b1b',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    {sharePointTestResult.includes('successful') ? (
                      <CheckCircle size={18} />
                    ) : (
                      <XCircle size={18} />
                    )}
                    <span>{sharePointTestResult}</span>
                  </div>
                )}

                {isSaved && (
                  <div className="success-message">
                    <span className="success-icon">✓</span>
                    Saved. Organization settings and the selected product (if any) were written to
                    the server.
                  </div>
                )}

                {process.env.NODE_ENV === 'development' && (
                  <div
                    style={{
                      flex: '1 1 100%',
                      width: '100%',
                      marginTop: 12,
                      padding: 10,
                      background: 'var(--color-bg-elevated, #f3f4f6)',
                      border: '1px solid var(--color-border, #e5e7eb)',
                      borderRadius: 8,
                      fontSize: 11,
                      fontFamily: 'ui-monospace, monospace',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 6,
                        gap: 8,
                      }}
                    >
                      <span style={{ color: 'var(--color-text-secondary, #6b7280)' }}>
                        Save debug log (same lines as <code>[SettingsSave]</code> in Console — use
                        filter <b>All levels</b>)
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setSaveDiagLines([]);
                          if (typeof window !== 'undefined') {
                            (
                              window as Window & { __settingsSaveDiag?: string[] }
                            ).__settingsSaveDiag = [];
                          }
                        }}
                        style={{
                          fontSize: 11,
                          padding: '4px 8px',
                          cursor: 'pointer',
                          borderRadius: 4,
                          border: '1px solid var(--color-border, #ccc)',
                          background: 'var(--color-bg-surface, #fff)',
                        }}
                      >
                        Clear
                      </button>
                    </div>
                    <pre
                      style={{
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: 180,
                        overflow: 'auto',
                        color: 'var(--color-text, #111)',
                      }}
                    >
                      {saveDiagLines.length === 0
                        ? '— No events yet. Click Save all above. If nothing appears here, something is blocking the button (extension overlay).'
                        : saveDiagLines.join('\n')}
                    </pre>
                  </div>
                )}
              </div>
            ) : null}
            {saveError && (activeTab === 'project' || activeTab === 'integrations') && (
              <div className="error-message" style={{ width: '100%', marginTop: 8 }} role="alert">
                <span className="error-icon">!</span>
                {saveError}
              </div>
            )}
          </div>
        )}

        {/* Team Management Tab */}
        {activeTab === 'team' && (
          <TeamTab value={teamContextValue}>
            {canManageOrgTeam ? (
              <div className="profile-card">
                <div className="card-section">
                  <h2 className="section-title">
                    <Users className="section-icon" />
                    Team Management
                  </h2>
                  <p className="section-description">
                    The <strong>team roster</strong> lists people by name for capacity, Releases,
                    Analytics, Forecast, and reports. Roster entries are <strong>not</strong> Relix
                    accounts by themselves. To give someone access to log in to Relix, use{' '}
                    <strong>Invite to organization</strong> (or they sign up and you add them to the
                    org). Use the <strong>Role</strong> column so each person is a{' '}
                    <strong>Developer</strong> or <strong>QA</strong> (or a custom role mapped under{' '}
                    <strong>Roles &amp; capacity categories</strong>): Forecast needs at least one
                    QA for a realistic Dev/QA split and for &quot;Suggest from history&quot; from
                    Jira worklogs.
                  </p>

                  <TeamTabAnchorStrip />

                  <section
                    id="team-zone-access"
                    className="settings-zone"
                    aria-labelledby="team-zone-access-h2"
                  >
                    <div className="settings-zone__header">
                      <h2 id="team-zone-access-h2" className="settings-zone__title">
                        Access &amp; permissions
                      </h2>
                    </div>
                    {showInviteSection && <InviteToOrgPanel />}
                    <OrgRbacMembersPanel />
                  </section>

                  <section
                    id="team-zone-capacity"
                    className="settings-zone"
                    aria-labelledby="team-zone-capacity-h2"
                  >
                    <div className="settings-zone__header">
                      <h2 id="team-zone-capacity-h2" className="settings-zone__title">
                        Capacity foundations
                      </h2>
                    </div>
                    <CapacityZonePanel />
                  </section>

                  <section
                    id="team-zone-people"
                    className="settings-zone"
                    aria-labelledby="team-zone-people-h2"
                  >
                    <div className="settings-zone__header">
                      <h2 id="team-zone-people-h2" className="settings-zone__title">
                        People &amp; roster
                      </h2>
                    </div>
                    {loadingTeamMembers ? (
                      <div
                        style={{
                          padding: '20px',
                          textAlign: 'center',
                          color: 'var(--color-text-secondary)',
                        }}
                      >
                        Loading team members...
                      </div>
                    ) : (
                      <>
                        {/* Suggest members from synced worklogs / issues (DB-only, no custom Jira fields) */}
                        <SuggestPeoplePanel />

                        <AddTeamMemberPanel />

                        <RolesAndCategoriesPanel />

                        <TeamRosterPanel />
                      </>
                    )}
                  </section>
                </div>
              </div>
            ) : (
              <div className="profile-card" style={{ padding: 'var(--space-8)' }}>
                <RolesAndCategoriesPanel />
              </div>
            )}
          </TeamTab>
        )}

        {activeTab === 'billing' && canViewBilling && (
          <div className="profile-card">
            <div className="card-section">
              <h2 className="section-title">
                <CreditCard className="section-icon" />
                Billing
              </h2>
              <p className="section-description">
                Subscription, usage, and invoices for your organization.
              </p>
              {subscription?.status === 'trialing' && subscription.trial_ends_at ? (
                <div
                  className="success-message"
                  role="status"
                  style={{ marginBottom: 16, width: '100%' }}
                >
                  <span className="success-icon">✓</span>
                  Free trial active until{' '}
                  {new Date(subscription.trial_ends_at).toLocaleDateString(undefined, {
                    dateStyle: 'medium',
                  })}
                  .
                </div>
              ) : null}
              <div className="form-group">
                <span className="form-label">Current plan</span>
                <p style={{ margin: '4px 0', fontWeight: 600 }}>
                  {subscription?.plan_name || '—'}{' '}
                  <span style={{ color: 'var(--color-text-secondary)', fontWeight: 400 }}>
                    ({subscription?.plan_slug || '—'})
                  </span>
                </p>
                <p style={{ margin: '4px 0', fontSize: 14, color: 'var(--color-text-secondary)' }}>
                  Status: <strong>{subscription?.status || '—'}</strong>
                  {subscription?.billing_cycle ? (
                    <> · Billing: {subscription.billing_cycle}</>
                  ) : null}
                </p>
                {subscription?.current_period_end ? (
                  <p
                    style={{ margin: '4px 0', fontSize: 14, color: 'var(--color-text-secondary)' }}
                  >
                    Next period ends:{' '}
                    {new Date(subscription.current_period_end).toLocaleDateString(undefined, {
                      dateStyle: 'medium',
                    })}
                  </p>
                ) : null}
              </div>
              {usage && subscription ? (
                <div className="form-group">
                  <span className="form-label">Usage</span>
                  <div style={{ marginTop: 8 }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 13,
                        marginBottom: 4,
                      }}
                    >
                      <span>Products</span>
                      <span>
                        {usage.products} / {subscription.max_products}
                      </span>
                    </div>
                    <div
                      style={{
                        height: 8,
                        borderRadius: 4,
                        background: 'var(--color-bg-subtle)',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          width: `${Math.min(100, (usage.products / Math.max(1, subscription.max_products)) * 100)}%`,
                          background:
                            'linear-gradient(90deg, var(--color-accent), var(--color-accent-2))',
                        }}
                      />
                    </div>
                  </div>
                  <div style={{ marginTop: 16 }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 13,
                        marginBottom: 4,
                      }}
                    >
                      <span>Team members</span>
                      <span>
                        {usage.members} / {subscription.max_team_members}
                      </span>
                    </div>
                    <div
                      style={{
                        height: 8,
                        borderRadius: 4,
                        background: 'var(--color-bg-subtle)',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          width: `${Math.min(100, (usage.members / Math.max(1, subscription.max_team_members)) * 100)}%`,
                          background:
                            'linear-gradient(90deg, var(--color-accent-2), var(--color-accent))',
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : null}
              {billingStripeSyncing ? (
                <p
                  style={{
                    color: 'var(--color-text-secondary)',
                    fontSize: 13,
                    margin: '0 0 12px',
                  }}
                >
                  Updating subscription from Stripe…
                </p>
              ) : null}
              <div className="card-actions" style={{ flexWrap: 'wrap', gap: 12 }}>
                {canModifyBilling ? (
                  <button
                    type="button"
                    className="save-button"
                    disabled={billingPortalLoading || billingStripeSyncing}
                    onClick={async () => {
                      setBillingPortalLoading(true);
                      try {
                        const res = await profileApiFetch('/api/billing/portal', {
                          method: 'POST',
                        });
                        const data = await res.json();
                        if (data.url) window.location.href = data.url;
                      } catch {
                        /* ignore */
                      } finally {
                        setBillingPortalLoading(false);
                      }
                    }}
                  >
                    {billingPortalLoading ? (
                      <Loader2 className="button-icon profile-settings-spin" />
                    ) : (
                      <CreditCard className="button-icon" />
                    )}
                    {billingPortalLoading ? 'Opening…' : 'Manage subscription'}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="save-button"
                  style={{ background: 'transparent', border: '1px solid var(--color-border)' }}
                  onClick={() => navigate('/pricing')}
                  disabled={billingStripeSyncing}
                >
                  Change plan
                </button>
                <button
                  type="button"
                  className="save-button"
                  style={{ background: 'transparent', border: '1px solid var(--color-border)' }}
                  disabled={billingStripeSyncing}
                  onClick={() => {
                    void (async () => {
                      setBillingStripeSyncing(true);
                      try {
                        await profileApiFetch('/api/billing/sync-subscription', { method: 'POST' });
                        await refreshSubscription();
                        const invRes = await profileApiFetch('/api/billing/invoices');
                        if (invRes.ok) {
                          const data = (await invRes.json()) as {
                            invoices?: typeof billingInvoices;
                          };
                          setBillingInvoices(data.invoices || []);
                        }
                      } catch {
                        /* ignore */
                      } finally {
                        setBillingStripeSyncing(false);
                      }
                    })();
                  }}
                >
                  {billingStripeSyncing ? (
                    <Loader2 className="button-icon profile-settings-spin" size={16} />
                  ) : null}
                  {billingStripeSyncing ? 'Syncing…' : 'Refresh status'}
                </button>
              </div>
            </div>
            <div className="card-section">
              <h3 className="section-title" style={{ fontSize: '1.1rem' }}>
                Invoices
              </h3>
              {billingInvoicesLoading ? (
                <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>
              ) : billingInvoices.length === 0 ? (
                <p style={{ color: 'var(--color-text-secondary)' }}>No invoices yet.</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: 8 }}>Date</th>
                        <th style={{ textAlign: 'left', padding: 8 }}>Amount</th>
                        <th style={{ textAlign: 'left', padding: 8 }}>Status</th>
                        <th style={{ textAlign: 'left', padding: 8 }}>PDF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billingInvoices.map((inv) => (
                        <tr key={inv.id}>
                          <td style={{ padding: 8, fontSize: 14 }}>
                            {new Date(inv.created_at).toLocaleDateString()}
                          </td>
                          <td style={{ padding: 8, fontSize: 14 }}>
                            {new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: inv.currency?.toUpperCase() || 'USD',
                            }).format(inv.amount_cents / 100)}
                          </td>
                          <td style={{ padding: 8, fontSize: 14 }}>{inv.status}</td>
                          <td style={{ padding: 8, fontSize: 14 }}>
                            {inv.invoice_pdf_url ? (
                              <a href={inv.invoice_pdf_url} target="_blank" rel="noreferrer">
                                View
                              </a>
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Jira / SharePoint — only on integration tabs; hidden on Billing (CSS order:-1 would pin it above billing content) */}
        {activeTab !== 'billing' && (
          <div className="collapsible-info-block-wrap">
            <CollapsibleInfoBlock
              title="Important Information"
              defaultCollapsed={true}
              items={[
                'Your credentials are stored locally in your browser',
                'API tokens are used to securely connect to Jira and SharePoint',
                'Never share your API tokens with anyone',
                'You can revoke tokens at any time from your Atlassian or SharePoint account',
                'Changes will apply immediately to all API requests',
                'SharePoint calendar sync requires a valid API key and site URL',
              ]}
            />
          </div>
        )}
      </div>
    </div>
  );
};
